// Amsterdam, "Bridge lights on the canal". A mild late-spring night at water level on a Prinsengracht quay,
// beside a moored houseboat. Across the canal stands a row of narrow, slightly leaning canal houses (step,
// bell, neck and spout gables, hoisting beams with hooks, tall white-framed windows), a brown cafe on the
// corner, elms and canal lamps, and behind them the Westerkerk tower with its blue-and-gold imperial crown.
// A hump-backed bridge over a side canal is outlined with warm bulbs that shimmer in the water. Cyclists
// cross it, a small open boat with a lantern glides by, ducks paddle, a grey heron keeps watch on the
// houseboat roof, the carillon plays on the quarters and elm seeds drift down at three depths. A breeze from the left
// stirs the elms and the houseboat's curtains, thin cloud drifts over the vane and behind the gables, moths loop round
// the canal lamps and now and then someone comes to a lit window.
// The city's billboard is a lit enamel ad board screwed across the upper floors of two houses left of the tower.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll,
  hash, rng, bayer, hex, makeCanvas, makeHalo, drawHalo, text, reflect, streak, makePitch, gradeAd, hands } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const BASE = 184;                                   // the house fronts meet the quay street
const STREET = 186;                                 // bicycle wheels on the far quay street
const EDGE = 187;                                   // quay coping: trees, lamps and bollards stand on it
const WL = 192;                                     // far waterline
const NEARQ = 258;                                  // near quay edge (bottom strip)
const GF = 164, FLH = 15;                           // top of the ground floor, height of an upper floor
const TWR = { cx: 224 };                            // the Westertoren
const GAP = { x0: 270, x1: 350 };                   // side canal between the two rows of houses
const BR = { x0: 256, x1: 364, cx: 310, rise: 10, ax: 24, crown: 181 };   // hump-backed bridge and its arch
const VPX = 236, YH = 190;                          // eye point used to draw the side canal in perspective
const MOON = { x: 74, y: 40 };
const LAMPS = [86, 262, 358, 440];                  // canal lamps on the far quay
const TREES = [{ x: 34, cy: 147, rx: 25, ry: 17, s: 1 }, { x: 132, cy: 144, rx: 27, ry: 19, s: 2 },
  { x: 414, cy: 146, rx: 26, ry: 18, s: 3 }];
const HB = { x0: -8, x1: 172, roof: 208, gun: 231, wl: 248 };   // the houseboat (left foreground)
const HERON = { x: 84, y: 207 };                    // heron sprite left edge, feet on the roof
const CAFE = { x0: 238, x1: 270 };
const BB = { x: 162, y: 124, w: 42, h: 21 };        // face of the enamel ad board across houses 7 and 8 (the city's billboard)
const BB_LAMPS = [BB.x + 6, BB.x + 21, BB.x + 35], BB_LAMP_Y = BB.y - 7;   // gooseneck lamps over the board
const BOAT_Y = 233;                                 // waterline of the passing boat
/** bridge deck: a smooth hump between the two quays */
const deckY = x => {
  const u = (x - BR.cx) / ((BR.x1 - BR.x0) / 2);
  return Math.abs(u) >= 1 ? STREET : STREET - BR.rise * 0.5 * (1 + Math.cos(Math.PI * u));
};
/** top of the arch opening at column x (null outside the arch) */
const archY = x => {
  const u = (x + 0.5 - BR.cx) / BR.ax;
  return Math.abs(u) >= 1 ? null : WL - 1 - (WL - 1 - BR.crown) * Math.sqrt(1 - u * u);
};

/* ================= Timed events (loop seconds) ================= */
const QUARTERS = [15, 75, 135, 195];               // the carillon plays a short tune on the quarters
const NOTE_DT = 0.7;                                // 4 notes, 0.7 s apart (matches the chime sound)
const HOUR = [199.5, 203, 206.5];                  // then the big bell strikes the hour
const BOAT = { t0: 96, t1: 168 };                  // the little open boat glides right to left
const DIVE = { t0: 176, t1: 182.5 };               // a duck dives (splash)
const RIDERS = [                                    // cyclists along the quay and over the bridge
  { a: 6, b: 30, dir: 1, coat: '#a8423a', hair: '#3a2418', kind: 0 },
  { a: 40, b: 61, dir: -1, coat: '#3a5a8a', hair: '#d8b070', kind: 1 },
  { a: 66, b: 94, dir: 1, coat: '#c8b890', hair: '#2a1a14', kind: 2 },        // cargo bike with a child
  { a: 112, b: 134, dir: -1, coat: '#2e3440', hair: '#6a4a30', kind: 0 },
  { a: 146, b: 170, dir: 1, coat: '#7a4a8a', hair: '#e0c080', kind: 1 },
  { a: 184, b: 206, dir: -1, coat: '#c87a3a', hair: '#1a1210', kind: 0 },
  { a: 214, b: 236, dir: 1, coat: '#4a7a5a', hair: '#8a5a38', kind: 1 },
];
const HERON_T = [                                   // [t0, t1, pose] for the heron on the houseboat (else 'stand')
  [22, 27.5, 'look'], [42, 70, 'hunch'], [75.4, 84, 'alert'], [104, 112, 'preen'], [120, 150, 'oneleg'],
  [160, 166.5, 'preen'], [172, 194, 'hunch'], [199.8, 213, 'alert'], [226, 231, 'look'],
];
const CURTAIN_PASS = [[33, 37], [118, 121.5], [171, 175]];   // someone crosses behind a lit curtain
// someone comes to a lit window and looks out over the canal: [t0, t1, how far along the row (0 left .. 1 right)]
const LOOK_T = [[22, 36, 0.13], [57, 70, 0.84], [86, 101, 0.33], [124, 137, 0.95], [151, 165, 0.02], [209, 223, 0.66]];
const TV_ON = [52, 228];                            // a television flickers in one window
const READER_OFF = [141, 187];                      // a reading lamp goes off, then on again
const BOATLAMP_FLICK = [[128, 129.2], [150.3, 151]];

/* ================= The breeze ================= */
// One breeze for everything it stirs: 0 in a lull .. 1 at the height of a gust (three slow swells, 48, 18.5 and 7.7 s).
// It comes from the left, the way the elm seeds and the stove's smoke drift, and runs along the quay at BREEZE_V px a
// second, so a crown answers a little after the one upwind of it and a gust is seen to pass.
const BREEZE_V = 45;
const breeze = (t, x = 0) => {
  const u = t - x / BREEZE_V;
  return clamp(0.4 + 0.3 * wave(u, 5, 0.12) + 0.2 * wave(u, 13, 0.47) + 0.12 * wave(u, 31, 0.8), 0, 1);
};

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = C(a), B = C(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2]]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
/** a canvas of just these pixels ([x, y, colour]) and where it goes (build time) */
function cutout(pts) {
  let x0 = W, y0 = H, x1 = 0, y1 = 0;
  for (const [x, y] of pts) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  const p = pix(x1 - x0 + 1, y1 - y0 + 1);
  for (const [x, y, col] of pts) p.set(x - x0, y - y0, col);
  return { c: p.done(), x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const box = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
/** darken an already painted pixel towards a tint (build time) */
function darken(p, x, y, f, tint = [6, 8, 18]) {
  if (x < 0 || y < 0 || x >= p.w || y >= p.h || !p.alpha(x, y)) return;
  const c = p.get(x, y);
  p.set(x, y, [0, 1, 2].map(i => Math.round(lerp(c[i], tint[i], f))));
}

/* ================= Palettes ================= */
const SKY = ['#050919', '#070c1f', '#090f25', '#0b122b', '#0e1631', '#111a38', '#141e3f', '#182346', '#1c284d',
  '#212d54', '#26335a', '#2c385f', '#333d63', '#3b4266', '#444767', '#4d4b66'];
const STONE = ['#33271f', '#4e3c2c', '#6e573d', '#907350', '#b09064', '#ccad7a', '#e4c890', '#f6e2b0'];
const TBRICK = ['#2a1410', '#401e16', '#58281c', '#703424', '#8a422c', '#a2553a'];
const BLUE = ['#0c1a48', '#14286c', '#1e3a92', '#2c52b8', '#4474d8', '#74a0f0'];
const GOLD = ['#553610', '#855816', '#b48024', '#dca83a', '#f6d066', '#fff0b0'];
const PALS = {
  brick: ['#1e0f0e', '#2b1513', '#3a1c18', '#4a241e', '#5c2e24', '#70392b', '#86462f'],
  brown: ['#1a1311', '#261b17', '#33241d', '#422e24', '#53392b', '#664634', '#7a553d'],
  dark: ['#0f0f15', '#15151d', '#1b1b25', '#23232e', '#2c2c39', '#373745', '#444353'],
  green: ['#0d1513', '#131d1b', '#192624', '#20302d', '#293b37', '#344843', '#415650'],
  cream: ['#25252e', '#302f39', '#3c3a44', '#4a4750', '#5a555c', '#6c6568', '#807774'],
  grey: ['#1a1e2a', '#222634', '#2b2f3e', '#353949', '#414555', '#4f5263', '#5e6072'],
};
const TRIM = ['#3e4252', '#565a6a', '#72747f', '#908f96', '#aeaaa8', '#cac2b6'];
const WARMWIN = [['#fff0c4', '#ffd88a', '#f6b660', '#d8903e'], ['#ffe2a0', '#f8c06a', '#e89a4a', '#c07430'],
  ['#fff6dc', '#f4dcaa', '#dcba80', '#b8925a']];
const LEAF = ['#070d0a', '#0b140e', '#101c13', '#162618', '#1e311d', '#283d22', '#354a27', '#46592c'];
const LEAFLIT = ['#2e3a1c', '#46532a', '#626e34', '#838a42', '#a4a452'];

/* ================= Sky, stars, moon, thin clouds ================= */
function renderSky() {
  const p = pix(W, 200);
  for (let y = 0; y < 200; y++) for (let x = 0; x < W; x++) {
    let v = Math.pow(clamp(y / 192, 0, 1), 1.35) * 0.78;
    v += Math.max(0, 1 - Math.hypot((x - MOON.x) / 110, (y - MOON.y) / 70)) * 0.17;      // moonlit haze
    v += Math.max(0, 1 - Math.hypot((x - 250) / 300, (y - 196) / 70)) * 0.16;          // city glow low down
    p.set(x, y, pick(SKY, v, x, y));
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(11);
  while (STARS.length < 56) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 118);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 24) continue;
    if (x > 196 && x < 252 && y > 6) continue;                                    // keep the tower's sky clean
    const b = r();
    STARS.push({ x, y, k: 9 + Math.floor(r() * 31), ph: r(), b: 0.2 + b * b * 0.7, big: b > 0.93 });
  }
}
/** waxing crescent with a faint earthshine */
function renderMoon() {
  const R = 7, s = 17, c0 = 8, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - c0, dy = y - c0, d = Math.hypot(dx, dy);
    if (d > R + 0.35) continue;
    const dark = Math.hypot(dx + 4.2, dy + 1.2) < R + 0.2;
    let col = dark ? '#172039' : '#f2ecd4';
    if (!dark) {
      if (d > R - 0.8) col = '#d8d0b2';
      else if (Math.hypot(dx + 4.2, dy + 1.2) < R + 1.4) col = '#e2dabe';
      if ((dx - 4) ** 2 + (dy - 2) ** 2 < 1.3 || (dx - 3) ** 2 + (dy + 3) ** 2 < 0.8) col = '#d0c8aa';
    } else if (d > R - 0.8) col = '#1e2844';
    p.set(i, y, col);
  }
  return p.done();
}
/** a tileable band of thin, separate cloud streaks (period W) */
function makeWisps(seed, h, n, pal) {
  const r = rng(seed), S = [];
  for (let i = 0; i < n; i++) S.push({ x: r() * W, y: h * (0.3 + r() * 0.4), len: 14 + r() * 32, th: 1.2 + r() * 1.7, tilt: (r() - 0.5) * 0.08, ph: r() * TAU });
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = 0;
    for (const q of S) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y - dx * q.tilt - Math.sin(dx / q.len * 2.6 + q.ph) * 1.3, v = 1 - (dx / q.len) ** 2 - (dy / q.th) ** 2;
      if (v > best) best = v;
    }
    D[y * W + x] = best * (0.7 + 0.6 * vnoise(x / 8, y / 1.5, seed, 60)) + (vnoise(x / 3, y, seed + 1, 160) - 0.5) * 0.22;
  }
  const get = (x, y) => (y < 0 || y >= h) ? 0 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = D[y * W + x], th = 0.14 + (bayer(x, y) - 0.5) * 0.08;
    if (v <= th - 0.08) continue;
    if (v <= th) { if (bayer(x + 1, y + 2) < 0.35) p.set(x, y, pal.body, 70); continue; }
    const col = get(x, y - 1) <= th ? pal.top : get(x, y + 1) <= th ? pal.bot : pal.body;
    p.set(x, y, col, v > th + 0.2 ? 170 : 115);
  }
  return p.done();
}

/* ================= The Westertoren: brick tower, floodlit stone stages, the blue-and-gold imperial crown ================= */
// The real tower (87 m) dwarfs the ~20 m canal houses: its canvas is drawn TWY px up, so the vane nearly touches the
// top of the frame, and the houses at its foot are kept low (houses 8-10), so a tall brick shaft shows above them.
const TX0 = TWR.cx - 16, TWW = 33, TWH = 190, TWY = -4;
const CROWN = [                                     // the crown of Maximilian (13 px, rows 25..38): lobes, arch, circlet
  '......o......', '.....oGo.....', '..bb.GgG.bb..', '.bBBbGgGbBBb.', 'bBBBBGgGBBBBb', 'BBBBBGgGBBBBB', 'BBgBBGgGBBgBB',
  'BBgBBGgGBBgBB', 'BBgBBGgGBBgBB', 'bBgBbGgGbBgBb', 'GGGGGGGGGGGGG', 'GRGWGRGRGWGRG', 'GGGGGGGGGGGGG', '.ggggggggggg.',
];
const BELLS = [[0, 60], [-1, 64]];                  // bell silhouettes in the front opening of the bell stage (dx, top y)
function renderTower() {
  const p = pix(TWW, TWH), q = pix(TWW, TWH), cx = TWR.cx;
  const S = (dx, y, col) => p.set(cx + dx - TX0, y, col);
  const fl = y => 0.14 * smooth(20, 120, y);                           // the floodlight is stronger lower down
  const side = (dx, f) => dx < -f ? 0.08 : dx > f ? -0.22 : 0;         // left faces catch the moon, right ones in shade
  const st = (v, dx, y) => pick(STONE, v, cx + dx, y, 0.5);
  // brick body, rising well clear of the low houses at its foot: stone quoins and bands, a tall arched window,
  // a shorter one under it; the floodlights below make the brick warmer towards the roofs
  const BANDS = [99, 100, 113, 127, 142, 156];
  for (let y = 99; y < TWH; y++) for (let dx = -10; dx <= 10; dx++) {
    const ay = Math.abs(dx), glow = 0.1 * smooth(128, 158, y);
    let col;
    if (ay >= 9 && (ay === 10 || ((y - 99) >> 1) & 1)) col = st(0.6 + fl(y) + glow + (dx > 0 ? -0.34 : 0.04), dx, y);
    else if (BANDS.includes(y)) col = st((y === 99 ? 0.74 : 0.52) + glow + (dx > 4 ? -0.2 : 0), dx, y);
    else col = pick(TBRICK, 0.55 + fl(y) + glow + (dx > 4 ? -0.18 : dx < -5 ? 0.06 : 0) - (y % 3 === 2 ? 0.2 : 0), cx + dx, y, 0.5);
    S(dx, y, col);
  }
  const archWin = (y0, y1) => {
    for (let y = y0; y <= y1; y++) for (let dx = -2; dx <= 2; dx++) {
      const a = Math.abs(dx);
      if ((y === y0 && a > 0) || (y === y0 + 1 && a > 1)) continue;
      const inner = (y >= y0 + 2 && a <= 1) || (y === y0 + 1 && a === 0);
      S(dx, y, inner ? (y < y0 + 5 ? '#221824' : '#150f17') : st(0.56 + (dx > 0 ? -0.22 : 0), dx, y));
    }
  };
  archWin(102, 126); archWin(131, 140);
  // gallery on top of the brick
  for (let dx = -11; dx <= 11; dx++) {
    S(dx, 96, st(0.8 + side(dx, 6), dx, 96));
    S(dx, 97, (dx & 1) ? '#2a1e1a' : st(0.6 + side(dx, 6), dx, 97));
    S(dx, 98, st(0.4 + side(dx, 6), dx, 98));
  }
  // square stone stage with the clock
  for (let y = 76; y <= 95; y++) for (let dx = -10; dx <= 10; dx++) {
    if (y > 76 && Math.abs(dx) > 9) continue;
    let v = 0.46 + fl(y) + side(dx, 7) + 0.08 * (y - 78) / 18;
    if (y === 76) v = 0.8 + side(dx, 7) * 0.5; else if (y === 77) v = 0.26;
    else if (dx === -9) v = 0.8; else if (dx === -8) v = 0.62; else if (dx === 8) v = 0.3; else if (dx === 9) v = 0.18;
    else if (y === 92) v += 0.2; else if (y === 93) v -= 0.14;
    S(dx, y, st(v, dx, y));
  }
  for (let y = -6; y <= 6; y++) for (let dx = -6; dx <= 6; dx++) {
    const d = Math.hypot(dx, y);
    if (d > 5.3) continue;
    S(dx, 85 + y, d > 4.3 ? (dx + y < 0 ? GOLD[4] : GOLD[2]) : (dx + y < -2 ? BLUE[2] : BLUE[1]));
  }
  for (const [mx, my] of [[0, -3], [3, 0], [0, 3], [-3, 0]]) S(mx, 85 + my, GOLD[2]);
  // (the hands show Amsterdam's time, drawn per frame in drawTower)
  for (let y = 88; y <= 91; y++) for (const dx of [-5, 5]) S(dx, y, '#3a2a24');                        // garlands
  // the bell stage: an open octagon (front face 7 px, oblique faces 4 px)
  for (let y = 54; y <= 72; y++) for (let dx = -8; dx <= 8; dx++) {
    const a = Math.abs(dx);
    if (y > 55 && a > 7) continue;
    let v = 0.46 + fl(y) + side(dx, 3) + 0.1 * (y - 56) / 16;
    if (y === 54) v = 0.82 + side(dx, 3) * 0.5; else if (y === 55) v = 0.26;
    else if (a === 4 || a === 7) v += dx < 0 ? 0.22 : 0.1;                                                 // corner pilasters
    else if (y === 70) v += 0.22; else if (y === 71) v -= 0.12;
    const openF = a <= 2 && y >= 59 && y <= 69 || (y === 58 && a <= 1);
    const openO = (a === 5 || a === 6) && y >= 60 && y <= 69;
    if (openF || openO) {
      S(dx, y, y < 62 ? '#281a1a' : '#1a1216');
      q.set(cx + dx - TX0, y, y < 63 ? '#ffe6a4' : '#f4b45c');                                          // lit belfry
      continue;
    }
    S(dx, y, st(v, dx, y));
  }
  for (const [bx, by] of BELLS) {                                                                         // the bells
    for (const [ox, oy] of [[0, 0], [-1, 1], [0, 1], [1, 1], [-1, 2], [0, 2], [1, 2], [-2, 3], [-1, 3], [0, 3], [1, 3], [2, 3]]) {
      const x = cx + bx + ox, y = by + oy;
      if (Math.abs(bx + ox) > 2) continue;
      p.set(x - TX0, y, oy === 3 ? '#4a3420' : '#2e2016');
      q.set(x - TX0, y, ox < 0 ? '#8a5a2a' : '#5a3a1e');
    }
  }
  // balustrade + corner obelisks on the clock stage (in front of the octagon's foot)
  for (let dx = -9; dx <= 9; dx++) {
    S(dx, 73, st(0.78 + side(dx, 6), dx, 73));
    S(dx, 74, (dx & 1) ? '#2a1e1a' : st(0.58 + side(dx, 6), dx, 74));
    S(dx, 75, st(0.38 + side(dx, 6), dx, 75));
  }
  for (const dx of [-9, 9]) {
    const v = dx < 0 ? 0.74 : 0.36;
    S(dx, 66, GOLD[dx < 0 ? 4 : 2]);
    for (let y = 67; y <= 72; y++) S(dx, y, st(v + (y > 70 ? 0.06 : 0), dx, y));
    S(dx + (dx < 0 ? 1 : -1), 71, st(v - 0.1, dx, 71)); S(dx + (dx < 0 ? 1 : -1), 72, st(v - 0.1, dx, 72));
  }
  // balustrade under the lantern
  for (let dx = -7; dx <= 7; dx++) {
    S(dx, 51, st(0.8 + side(dx, 3), dx, 51));
    S(dx, 52, (dx & 1) ? '#2a1e1a' : st(0.6 + side(dx, 3), dx, 52));
    S(dx, 53, st(0.36 + side(dx, 3), dx, 53));
  }
  // the small octagonal lantern that carries the crown
  for (let y = 39; y <= 50; y++) for (let dx = -5; dx <= 5; dx++) {
    const a = Math.abs(dx);
    if (y > 39 && a > 4) continue;
    let v = 0.5 + fl(y) + side(dx, 1) + 0.1 * (y - 41) / 9;
    if (y === 39) v = 0.84 + side(dx, 1) * 0.5; else if (y === 40) v = 0.28;
    else if (a === 2 || a === 4) v += dx < 0 ? 0.2 : 0.08;
    if ((dx === 0 && y >= 43 && y <= 48) || (a === 3 && y >= 44 && y <= 48)) {
      S(dx, y, '#1e1418'); q.set(cx + dx - TX0, y, '#ffd890'); continue;
    }
    S(dx, y, st(v, dx, y));
  }
  // the imperial crown
  CROWN.forEach((row, j) => {
    for (let i = 0; i < 13; i++) {
      const ch = row[i], dx = i - 6, sh = dx < -2 ? 1 : dx > 2 ? -1 : 0;
      if (ch === '.') continue;
      let col;
      if (ch === 'B') col = BLUE[clamp(3 + sh - (j > 7 ? 1 : 0), 0, 5)];
      else if (ch === 'b') col = BLUE[clamp(1 + sh, 0, 5)];
      else if (ch === 'G') col = GOLD[clamp(4 + sh, 0, 5)];
      else if (ch === 'g') col = GOLD[clamp(2 + sh, 0, 5)];
      else if (ch === 'o') col = GOLD[5];
      else if (ch === 'R') col = sh < 0 ? '#8a1c24' : '#d8303a';
      else col = '#f4f0e8';
      S(dx, 25 + j, col);
    }
  });
  S(-4, 29, BLUE[5]); S(-3, 28, BLUE[5]); S(4, 29, BLUE[4]);                                           // glints
  // orb, needle, weathervane
  for (let y = 20; y <= 24; y++) S(0, y, y > 22 ? GOLD[3] : GOLD[2]);
  S(-1, 24, GOLD[4]); S(1, 24, GOLD[2]);
  for (const [dx, y, k] of [[0, 16, 4], [-1, 17, 5], [0, 17, 4], [1, 17, 2], [-1, 18, 4], [0, 18, 3], [1, 18, 2], [0, 19, 2]]) S(dx, y, GOLD[k]);
  for (let y = 7; y <= 15; y++) S(0, y, y === 7 ? GOLD[4] : '#8a6a3a');
  for (const [dx, y] of [[1, 9], [2, 9], [3, 9], [4, 9], [1, 10], [2, 10], [3, 10], [1, 11], [2, 11]]) S(dx, y, dx === 1 ? GOLD[3] : GOLD[2]);
  return { c: p.done(), lit: q.done() };
}

/* ================= The canal houses across the water =================
   Painted twice with identical facades: once with every window dark, once with every window lit (each with
   its own interior). Per frame the dark row is drawn and the lit windows are copied over from the lit row. */
const HOUSES = [
  { x0: -3, w: 25, up: 4, g: 'cornice', gh: 8, pal: 'brick', lean: 0, door: 0 },
  { x0: 22, w: 19, up: 4, g: 'neck', gh: 20, pal: 'dark', lean: 1, door: 1 },
  { x0: 41, w: 27, up: 4, g: 'step', gh: 22, pal: 'brick', lean: 0, door: 2 },
  { x0: 68, w: 18, up: 4, g: 'bell', gh: 18, pal: 'cream', lean: -1, door: 0 },
  { x0: 86, w: 26, up: 4, g: 'spout', gh: 19, pal: 'brown', lean: 0, door: 0, ware: true },
  { x0: 112, w: 22, up: 3, g: 'neck', gh: 20, pal: 'green', lean: 2, door: 2 },
  { x0: 134, w: 26, up: 4, g: 'cornice', gh: 8, pal: 'grey', lean: 0, door: 0 },
  { x0: 160, w: 20, up: 3, g: 'bell', gh: 18, pal: 'brick', lean: -1, door: 1 },
  { x0: 180, w: 26, up: 3, g: 'step', gh: 18, pal: 'dark', lean: 0, door: 2 },        // 8-10: kept low around the tower
  { x0: 206, w: 32, up: 1, g: 'cornice', gh: 6, pal: 'cream', lean: 0, door: 1 },     // the low house at its foot
  { x0: 238, w: 32, up: 2, g: 'step', gh: 16, pal: 'brick', lean: 0, cafe: true },
  { x0: 350, w: 26, up: 4, g: 'neck', gh: 20, pal: 'brick', lean: 0, door: 0 },
  { x0: 376, w: 21, up: 4, g: 'bell', gh: 18, pal: 'dark', lean: 1, door: 2 },
  { x0: 397, w: 28, up: 4, g: 'step', gh: 22, pal: 'brown', lean: 0, door: 1 },
  { x0: 425, w: 20, up: 4, g: 'cornice', gh: 7, pal: 'cream', lean: -1, door: 0 },
  { x0: 445, w: 24, up: 3, g: 'neck', gh: 20, pal: 'green', lean: 0, door: 2 },
  { x0: 469, w: 15, up: 4, g: 'spout', gh: 14, pal: 'brick', lean: 0, door: 0, ware: true },
];
const LIGHTS = [...LAMPS.map(x => [x, 162, 1]), [254, 176, 0.9]];   // warm spill on the facades (lamps, cafe)
const lampTerm = (x, y) => {
  let s = 0;
  for (const [lx, ly, k] of LIGHTS) s += k * Math.exp(-Math.hypot((x - lx) / 26, (y - ly) / 24));
  return Math.min(1, s);
};
/** half-width of a gable k px above the cornice line (k >= 1), or -1 above the gable */
function gableHW(h, k) {
  const { w, gh } = h, s = (k - 0.5) / gh;
  if (k > gh) return -1;
  if (h.g === 'step') {
    const n = w >= 24 ? 4 : 3, tw = Math.max(2, Math.round(w * 0.14)), i = Math.min(n, Math.floor((k - 1) / (gh / (n + 1))));
    return w / 2 - i * (w / 2 - tw) / n;
  }
  if (h.g === 'bell') {
    const top = Math.max(2.5, w * 0.2);
    return s <= 0.7 ? top + (w / 2 - top) * Math.pow(1 - s / 0.7, 1.7) : top * Math.sqrt(Math.max(0, 1 - ((s - 0.7) / 0.3) ** 2)) + 0.6;
  }
  if (h.g === 'neck') {
    const nw = Math.max(2.5, w * 0.2), sh = gh * 0.42;
    if (k <= sh) return nw + (w / 2 - nw) * Math.sqrt(Math.max(0, 1 - (k / sh) ** 2));
    if (k <= gh - 4) return k === gh - 4 ? nw + 1 : nw;
    return (nw + 1) * (gh - k + 0.6) / 4.6;
  }
  if (h.g === 'spout') return k >= gh - 1 ? 1.5 : Math.max(1.5, w / 2 * (1 - s) + 0.5);
  if (k <= 2) return w / 2 + 1;                                                     // cornice: overhanging band
  const s2 = (k - 2) / (gh - 2);
  return w * 0.23 * Math.sqrt(Math.max(0, 1 - s2 * s2)) + 0.6;                       // ...and a little crest
}
const WINS = [];                                    // every window of the row: glass rect and light schedule
const WALK_WINS = [];                                // a row of curtained windows someone walks past
let TV_WIN = null, READER_WIN = null;
function renderRow() {
  const pD = pix(W, H), pL = pix(W, H), r = rng(2024);
  const both = (x, y, col) => { pD.set(x, y, col); pL.set(x, y, col); };
  HOUSES.forEach((h, hi) => {
    const pal = PALS[h.pal], palW = pal.map(c => mixHex(c, '#9a6234', 0.28)), top = GF - FLH * h.up, apex = top - h.gh;
    const cxl = h.w / 2, off = y => Math.round(h.lean * (BASE - y) / (BASE - apex));
    const inside = (u, y) => {
      if (y >= top) return u >= 0 && u < h.w;
      const hw = gableHW(h, top - y);
      return hw >= 0 && Math.abs(u + 0.5 - cxl) <= hw;
    };
    const shade = (x, y) => {
      const lt = lampTerm(x, y);
      return { v: 0.18 + 0.28 * smooth(90, 184, y) + 0.38 * lt + (vnoise(x / 3, y / 2, hi) - 0.5) * 0.06, lt };
    };
    // facade and gable
    for (let y = apex - 1; y < BASE; y++) for (let u = -1; u <= h.w; u++) {
      if (!inside(u, y)) continue;
      const x = h.x0 + u + off(y);
      let { v, lt } = shade(x, y);
      if (u === 0) v -= 0.12;                                                            // party wall shadow
      if (y < top && !inside(u - 1, y)) v += 0.14;                                      // moonlit left edge of the gable
      let col = lt + (bayer(x, y) - 0.5) * 0.25 > 0.42 ? pick(palW, v, x, y, 0.6) : pick(pal, v, x, y, 0.6);
      if (y < top) {                                                                     // stone trim on the gables
        const k = top - y, edge = !inside(u, y - 1) || !inside(u - 1, y) || !inside(u + 1, y);
        const tv = 0.36 + 0.3 * lt + (u < cxl ? 0.12 : 0);
        if (h.g === 'step' && !inside(u, y - 1)) col = pick(TRIM, tv + 0.1, x, y);
        else if ((h.g === 'bell' || h.g === 'spout') && edge) col = pick(TRIM, tv - (h.g === 'spout' ? 0.2 : 0), x, y);
        else if (h.g === 'neck' && (Math.abs(u + 0.5 - cxl) > Math.max(2.5, h.w * 0.2) + 0.5 || k === h.gh - 4 || (k > h.gh - 4 && edge))) col = pick(TRIM, tv, x, y);
        else if (h.g === 'cornice' && (k <= 2 || edge)) col = pick(TRIM, k === 1 ? tv - 0.16 : tv + 0.12, x, y);
      }
      if (y === GF && !h.cafe) col = pick(pal, v - 0.22, x, y);                        // beam over the ground floor
      both(x, y, col);
    }
    if (h.g === 'step') both(h.x0 + Math.floor(cxl) + off(apex - 1), apex - 1, TRIM[3]);   // finial
    // windows
    const ncol = h.w >= 30 ? 4 : h.w >= 21 ? 3 : 2;
    const m = Math.floor((h.w - ncol * 5) / (ncol + 1)), span = ncol * 5 + (ncol - 1) * m, st = Math.floor((h.w - span) / 2);
    const cols = Array.from({ length: ncol }, (_, i) => h.x0 + st + i * (5 + m));
    const frameAt = (fx, fy, gw, gh2, kind) => {
      const o = off(fy + (gh2 >> 1));
      for (let y = fy - 1; y <= fy + gh2; y++) for (let x = fx - 1; x <= fx + gw; x++) {
        const lt = lampTerm(x, y), glass = x >= fx && x < fx + gw && y >= fy && y < fy + gh2;
        if (!glass) { both(x + off(y), y, pick(TRIM, 0.34 + 0.4 * lt + 0.12 * smooth(100, 184, y), x, y)); continue; }
        const j = y - fy, X = x + off(y);
        pD.set(X, y, j === 0 ? '#1a2442' : j === 3 && gh2 > 5 ? pick(TRIM, 0.3 + 0.3 * lt, x, y) : lt > 0.5 && j === gh2 - 1 ? '#2e2420' : '#0b1024');
        pL.set(X, y, litGlass(kind, x - fx, j, gw, gh2));
      }
      let xmin = 99, xmax = -99;
      for (let y = fy; y < fy + gh2; y++) { xmin = Math.min(xmin, off(y)); xmax = Math.max(xmax, off(y)); }
      return { x: fx + xmin, y: fy, w: gw + xmax - xmin, h: gh2, cx: fx + o + (gw >> 1), cy: fy + (gh2 >> 1) };
    };
    const addWin = (rect, forced) => {
      const v = r();
      const mode = forced || (v < 0.36 ? 'on' : v < 0.62 ? 'off' : 'tog');
      const wn = { ...rect, id: WINS.length, mode, n: 2 + Math.floor(r() * 4), p: 0.4 + r() * 0.3, off: r() };
      WINS.push(wn);
      return wn;
    };
    const kindOf = () => { const v = r(); return v < 0.3 ? 0 : v < 0.46 ? 1 : v < 0.58 ? 2 : v < 0.72 ? 3 : v < 0.84 ? 4 : v < 0.94 ? 5 : 6; };
    for (let k = 0; k < h.up; k++) {
      const yT = GF - FLH * (k + 1), topFloor = k === h.up - 1;
      cols.forEach((fx, ci) => {
        const fy = yT + (topFloor ? 5 : 4), gh2 = topFloor ? 6 : 8;
        if (h.ware && ci === (ncol >> 1)) {                                                // warehouse loading doors
          for (let y = fy - 1; y <= fy + gh2; y++) for (let x = fx - 1; x <= fx + 3; x++) {
            const edge = x === fx - 1 || x === fx + 3 || y === fy - 1 || y === fy + gh2;
            both(x + off(y), y, edge ? '#2a1c14' : (x === fx + 1 ? '#1a3226' : (y + x) % 3 ? '#23402e' : '#1c3426'));
          }
          return;
        }
        const sp = hi === 9 && k === h.up - 1 ? 'walk' : hi === 13 && k === 2 && ci === 0 ? 'tv' : hi === 2 && k === 0 && ci === 1 ? 'reader' : null;
        const kind = sp === 'walk' ? 1 : sp === 'reader' ? 3 : sp === 'tv' ? 6 : kindOf();
        const wn = addWin(frameAt(fx, fy, 3, gh2, kind), sp === 'tv' ? 'off' : sp ? 'on' : undefined);
        wn.bare = !sp && (kind === 0 || kind === 3 || kind === 5);                              // no curtain, plant or screen in the way
        if (h.ware) for (let y = fy; y < fy + gh2; y++) {                                     // painted shutters
          both(fx - 2 + off(y), y, (y - fy) % 4 < 2 ? '#6a1c16' : '#1c3a2a');
          both(fx + 4 + off(y), y, (y - fy) % 4 < 2 ? '#1c3a2a' : '#6a1c16');
        }
        if (sp === 'walk') WALK_WINS.push(wn); else if (sp === 'tv') TV_WIN = wn; else if (sp === 'reader') READER_WIN = wn;
      });
    }
    // small window in the gable (not for cornice fronts)
    if (h.g !== 'cornice') {
      const gy = h.g === 'step' ? top - 11 : h.g === 'spout' ? top - 10 : top - 9, gx = h.x0 + Math.floor(cxl) - 1;
      if (h.ware) {
        for (let y = gy - 1; y <= gy + 5; y++) for (let x = gx - 1; x <= gx + 3; x++) both(x + off(y), y, x === gx - 1 || x === gx + 3 || y === gy - 1 || y === gy + 5 ? '#2a1c14' : '#23402e');
      } else addWin(frameAt(gx, gy, 3, 5, kindOf()), r() < 0.5 ? 'off' : undefined);
    }
    // hoisting beam with its hook, under the top of the gable
    const bx = h.x0 + Math.floor(cxl) - 1, by = h.g === 'cornice' ? top - h.gh + 2 : h.g === 'neck' ? apex + 5 : apex + 3;
    const bo = off(by);
    both(bx + bo, by, '#4e3220'); both(bx + 1 + bo, by, '#3a2416'); both(bx + bo, by + 1, '#2a1a10'); both(bx + 1 + bo, by + 1, '#1e120a');
    both(bx + 1 + bo, by + 2, '#6a6a72'); both(bx + 1 + bo, by + 3, '#6a6a72'); both(bx + 2 + bo, by + 4, '#8a8a92'); both(bx + 1 + bo, by + 4, '#55555c');
    // ground floor
    if (h.cafe) return;
    cols.forEach((fx, ci) => {
      if (ci === h.door) {
        for (let y = 167; y <= 183; y++) for (let x = fx - 1; x <= fx + 3; x++) {
          const edge = x === fx - 1 || x === fx + 3 || y === 167 || y === 170;
          let col = edge ? pick(TRIM, 0.4 + 0.4 * lampTerm(x, y), x, y) : y < 170 ? '#f0c878' : ['#1a3a2a', '#141418', '#5a1a18', '#1a2a4a'][hi % 4];
          if (!edge && y > 170 && (x === fx + 1) && (y === 174 || y === 179)) col = '#0a0a0e';
          both(x + off(y), y, col);
        }
        for (let x = fx - 2; x <= fx + 4; x++) { both(x, 182, pick(TRIM, 0.3 + 0.3 * lampTerm(x, 182), x, 182)); both(x, 183, pick(TRIM, 0.18 + 0.2 * lampTerm(x, 183), x, 183)); }
        both(fx - 2, 181, '#141414'); both(fx + 4, 181, '#141414');
      } else addWin(frameAt(fx, 168, 3, 11, kindOf()));
    });
  });
  paintCafe(pD, pL, both);
  paintSideCanal(both);
  // the side canal's elms stir too: their crowns are cut out of the finished row (so the lamps, trunks and leaves
  // painted over them stay out) and drawn again a pixel downwind when the breeze reaches them (drawRow)
  for (const E of SIDE_ELMS) {
    const keep = E.pts.filter(([x, y, col]) => x < GAP.x1 - 2 && pD.get(x, y).every((v, i) => v === C(col)[i])
      && !SIDE_LAMPS.some(l => Math.abs(x - l.x) <= 1 && y >= l.y - 1));
    E.o = keep.length ? cutout(keep) : null;
  }
  return { dark: trim(pD), lit: trim(pL) };
}
/** colour of lit glass for interior type `kind` at glass pixel (i, j) */
function litGlass(kind, i, j, gw, gh) {
  const ramp = WARMWIN[kind === 3 ? 2 : kind === 5 ? 1 : 0], f = j / (gh - 1);
  let col = f < 0.25 ? ramp[0] : f < 0.7 ? ramp[1] : ramp[2];
  if (j === 3 && gh > 5) col = '#e8cc98';                                                   // sash bar, backlit
  if (kind === 1) col = i === 1 ? '#d8b070' : j === 0 ? '#c89858' : '#f0d49a';            // cream curtain
  else if (kind === 2 && i !== 1) col = j < 2 ? '#b0503a' : '#8a3226';                      // red curtains half drawn
  else if (kind === 3 && j === 1 && i === 1) col = '#ffffff';                                // pendant lamp
  else if (kind === 4 && j === gh - 1 && i === 1) col = '#6a3a20';                          // a plant on the sill
  else if (kind === 4 && j === gh - 2) col = i === 1 ? '#3a5a2a' : '#243e1c';
  else if (kind === 4 && j === gh - 3 && i !== 2) col = '#2a4a22';
  else if (kind === 5 && (j === 2 || j === 5)) col = '#6a4020';                              // bookshelf
  else if (kind === 6) col = f < 0.4 ? '#dce4f4' : '#a8b8d8';                               // cool lamp
  return col;
}
/** the brown cafe on the corner by the bridge: fascia, big warm windows, regulars inside */
function paintCafe(pD, pL, both) {
  const { x0, x1 } = CAFE;
  for (let y = 162; y <= 183; y++) for (let x = x0; x < x1; x++) {
    let col = '#1e120c';
    if (y === 162) col = '#5a3a22'; else if (y === 168) col = '#3a2416';
    else if (y >= 169 && (x <= x0 + 1 || x >= x1 - 2)) col = x === x0 || x === x1 - 2 ? '#4a2e1a' : '#26160e';
    both(x, y, col);
  }
  const [tc, tx] = makeCanvas(16, 5);                                                         // lettering on the fascia
  text(tx, 'CAFE', 0, 0, '#f6cc7a');
  tx.getImageData(0, 0, 16, 5).data.forEach((v, i) => {
    if (i % 4 !== 3 || !v) return;
    const k = i >> 2, ty = Math.floor(k / 16);
    both(x0 + 9 + (k % 16), 163 + ty, ty === 0 ? '#ffe4a8' : '#f6cc7a');
  });
  void tc;
  const glass = (gx0, gx1) => {
    for (let y = 171; y <= 181; y++) for (let x = gx0; x <= gx1; x++) {
      const f = (y - 171) / 10;
      let col = f < 0.25 ? '#ffe0a0' : f < 0.65 ? '#f4b25e' : '#d0843e';
      if (y === 173) col = '#3a2214';                                                          // transom bar
      else if (y < 173 && (x - gx0) % 3 === 2) col = '#3a2214';
      else if (y === 176 && (x - gx0) % 2 === 0) col = ['#3a6a3a', '#8a2a24', '#c89a40', '#3a3a6a'][(x >> 1) % 4];   // bottles
      else if (y === 177) col = '#7a4420';
      both(x, y, col);
    }
  };
  glass(x0 + 3, x0 + 13); glass(x0 + 20, x0 + 29);
  for (let y = 170; y <= 183; y++) for (let x = x0 + 15; x <= x0 + 18; x++) {                // the door
    const g = x >= x0 + 16 && x <= x0 + 17 && y >= 172 && y <= 178;
    both(x, y, g ? (y < 175 ? '#ffd890' : '#e8a050') : x === x0 + 15 ? '#3a2416' : '#26160e');
  }
  for (let x = x0 + 3; x <= x0 + 29; x++) both(x, 182, '#140c08');
  const SIL = '#3a1e10';
  for (const [hx, hy] of [[x0 + 5, 175], [x0 + 10, 176], [x0 + 25, 175]]) {                  // regulars at the tables and the bar
    both(hx, hy, SIL); both(hx + 1, hy, SIL); both(hx, hy + 1, SIL); both(hx + 1, hy + 1, SIL);
    for (let y = hy + 2; y <= 181; y++) for (let x = hx - 1; x <= hx + 2; x++) both(x, y, SIL);
  }
  for (let x = x0 + 6; x <= x0 + 10; x++) both(x, 179, '#5a3218');                          // table
  both(x0 + 8, 178, '#ffd070'); both(x0 + 8, 177, '#fff4d0');                                // candle
}

/* ================= The side canal, seen in perspective between the two rows ================= */
const Z0 = 160;                                     // distance scale: world units at the far quay = 1 px
const SIDE = [];                                    // houses along the right bank of the side canal
{
  const r = rng(77);
  let d = 0;
  while (d < 900) {
    const w = 20 + Math.floor(r() * 10);
    SIDE.push({ d0: d, d1: d + w, top: r() < 0.55 ? 104 : 119, gh: 10 + Math.floor(r() * 10), step: r() < 0.5,
      pal: ['brick', 'dark', 'brown', 'cream', 'green'][Math.floor(r() * 5)] });
    d += w;
  }
}
const SIDE_LAMPS = [];
const SIDE_ELMS = [];                               // its elms, nearest first: what of each crown the breeze moves
const sideX = (lat, d) => VPX + lat * Z0 / (Z0 + d), sideY = (Yw, d) => YH + (Yw - YH) * Z0 / (Z0 + d);
function paintSideCanal(both) {
  const L = GAP.x1 - VPX, HAZE = '#1a2244';
  for (let x = GAP.x0; x < GAP.x1; x++) {
    const s = (x + 0.5 - VPX) / L, d = Z0 * (1 / s - 1);
    const hi = SIDE.findIndex(h => d >= h.d0 && d < h.d1);
    if (hi < 0) continue;
    const h = SIDE[hi], wd = h.d1 - h.d0, uu = d - h.d0, pal = PALS[h.pal], haze = clamp((1 - s) * 1.15, 0, 0.8);
    const peak = 1 - Math.abs(2 * uu / wd - 1);
    const topW = h.top - h.gh * (h.step ? Math.floor(peak * 3.99) / 3 : peak);
    const yTop = Math.round(YH + (topW - YH) * s);
    for (let y = yTop; y < 186; y++) {
      const Yw = YH + (y + 0.5 - YH) / s;
      let win = 0;
      if (Yw > h.top + 1 && Yw < GF) {
        const k = Math.floor((GF - Yw) / FLH), ly = Yw - (GF - FLH * (k + 1)), c = uu % 8;
        if (ly >= 4 && ly < 12 && c >= 3 && c < 6.5) win = hash(hi, k, Math.floor(uu / 8)) < 0.42 ? 2 : 1;
      } else if (Yw >= GF + 4 && Yw < 180 && uu % 8 >= 3 && uu % 8 < 6.5) win = hash(hi, 9, Math.floor(uu / 8)) < 0.5 ? 2 : 1;
      let col;
      if (win === 2) col = mixHex(ly2col(Yw), HAZE, haze * 0.5);
      else if (win === 1) col = mixHex('#0c1226', HAZE, haze * 0.4);
      else {
        let v = 0.28 + 0.22 * smooth(100, 184, Yw) + (hi & 1 ? 0.06 : 0);
        if (y === yTop) v += 0.3;
        if (uu < 0.9 * Z0 / (s * s * L)) v -= 0.15;                                               // party walls
        col = mixHex(pick(pal, v, x, y, 0.6), HAZE, haze);
      }
      both(x, y, col);
    }
  }
  // elms and canal lamps along that quay, shrinking into the distance
  for (const d of [66, 150, 250, 380]) {
    const cx = sideX(100, d), cy = sideY(148, d), R = 19 * Z0 / (Z0 + d), haze = clamp(d / 500, 0, 0.7), pts = [];
    for (let y = Math.floor(cy - R); y <= cy + R; y++) for (let x = Math.floor(cx - R * 1.1); x <= cx + R * 1.1; x++) {
      if (x < GAP.x0 || x >= GAP.x1) continue;
      const n = vnoise(x / 2.5, y / 2, d), dx = (x + 0.5 - cx) / (R * 1.1), dy = (y + 0.5 - cy) / R, v = 1 - dx * dx - dy * dy + (n - 0.5) * 0.8;
      if (v <= 0 || (v < 0.12 && bayer(x, y) > 0.5)) continue;
      const lit = clamp(dy * 0.9 + (vnoise(x / 1.6, y / 1.6, d + 5) - 0.5) * 0.9, 0, 1);
      const col = mixHex(lit > 0.5 ? pick(LEAFLIT, (lit - 0.5) * 1.4, x, y) : pick(LEAF, 0.15 + n * 0.6, x, y), HAZE, haze + 0.12);
      both(x, y, col);
      pts.push([x, y, col]);
    }
    SIDE_ELMS.push({ pts, cx, amp: Z0 / (Z0 + d), k: 21 + SIDE_ELMS.length * 6 });
    const ty = sideY(186, d);
    for (let y = Math.round(cy + R * 0.6); y < ty; y++) both(Math.round(cx), y, mixHex('#141010', HAZE, haze));
  }
  for (const d of [28, 104, 196, 310, 470]) {
    const lx = Math.round(sideX(110, d)), ly = Math.round(sideY(162, d)), by = Math.round(sideY(187, d));
    if (lx < GAP.x0 || lx >= GAP.x1) continue;
    for (let y = ly + 1; y < by; y++) both(lx, y, '#12141c');
    both(lx, ly, '#ffe2a0');
    SIDE_LAMPS.push({ x: lx, y: ly, a: clamp(Z0 / (Z0 + d), 0.3, 1) });
  }
}
const ly2col = Yw => Yw > GF ? '#f0b060' : Yw > 130 ? '#f6c46e' : '#ffd88a';

/* ================= The enamel ad board on houses 7 and 8 (the city's one billboard) =================
   An old white enamel board in a black iron frame, bolted across the second and third floors of two canal houses
   and lit by three small gooseneck lamps. Its own ad is Delft blue on white: "FIETS HUREN" (rent a bike) and an
   omafiets with a crate of tulips whose wheels and pedals turn. BB is the face.
   It is lit like the row it hangs on, not like a screen: the lamps are as warm and as bright as the houses' lit
   windows, the enamel under them is the ivory of a wall in lamplight (its ramp ends two steps short of the top of the
   tower's floodlit STONE) and falls to the dusk of the brick between and below them, and every ink on it (the blues, the
   red, the tulips, the spokes and pedals drawn per frame) is stepped down with it. Rust runs from the frame's top
   edge (gradeAd's wear). The tower beside it stays the brightest thing in the row. */
const IRON = { d: '#0b0c11', m: '#15171e', h: '#262833', l: '#3e3a40', w: '#5a4c40' };
const ENAMEL = ['#2a2c40', '#3c3a4c', '#524a52', '#6a5c56', '#82705c', '#998464', '#ae976e', '#c0a878'];
const DELFT = { d: '#121c56', m: '#1c3278', l: '#2a4088', s: '#34488a' };
const BB_LAMP = { core: '#f6cf86', rim: '#c8924c' };   // a lamp's lit opening: the colours of a lit window in the row
const BB_RUST = '#3a2418';                              // what the enamel weathers towards under the frame's top edge
const BIKE_AT = { x: 2, y: 4 };                     // the bike sprite's top-left in the face
const WHEELS = [[3, 9], [12, 9]], CRANK = [7, 10];  // hubs and bottom bracket, in the sprite
const TYRES = ['..ddd......ddd..', '.d...d....d...d.', 'd.....d..d.....d', 'd.....d..d.....d', 'd.....d..d.....d',
  '.d...d....d...d.', '..ddd......ddd..'];        // rows 6..12 of the sprite (the wheels, painted into the face)
const BIKE_TOP = [                                  // frame, saddle, rack, crate and tulips (drawn over the spokes)
  '............r.y.', '...........y.r.R', '........BB.gGgGg', '.......B..Bddddd', '....SSS...Bccccc', 'BBBB.B....Bddddd',
  '.....B....B.....', '....B.B..B.B....', '....B.B..B.B....', '...hB..BB...h...', '.....B.k........'];
const BIKE_KEY = { B: DELFT.m, S: DELFT.d, c: DELFT.l, h: DELFT.l, k: DELFT.d, d: DELFT.d,
  r: '#b02630', R: '#84161e', y: '#b88a26', Y: '#946c1c', g: '#2c6a30', G: '#6c9636' };
function renderBoard() {
  const p = pix(W, H), L = BB.x - 2, R = BB.x + BB.w + 1, T = BB.y - 2, B = BB.y + BB.h + 1;   // frame box
  const onWall = (x, y) => HOUSES.some(h => x >= h.x0 && x < h.x0 + h.w && y >= GF - FLH * h.up);   // not on the sky past house 8
  const shadow = (x, y, a) => { if (onWall(x, y)) p.set(x, y, [6, 8, 18], a); };
  for (let x = L + 1; x <= R + 1; x++) { shadow(x, B + 1, 150); shadow(x, B + 2, 70); }        // shadow on the brick
  for (let y = T + 2; y <= B; y++) shadow(R + 1, y, 90);
  for (let y = T; y <= B; y++) for (let x = L; x <= R; x++) {                     // the iron frame
    let col = IRON.m;
    if (y === T) col = x % 5 === 2 ? IRON.w : IRON.l;                            // top edge, lit by the lamps
    else if (y === B) col = IRON.d;
    else if (x === L) col = IRON.h;                                               // moonlit left edge
    else if (x === R) col = IRON.d;
    p.set(x, y, col);
  }
  for (const [bx, by] of [[L, B], [R, T], [R, B]]) p.set(bx, by, IRON.d);
  for (const [bx, by] of [[L + 1, T + 1], [R - 1, T + 1], [L + 1, B - 1], [R - 1, B - 1]]) p.set(bx, by, '#8a8478');   // bolt heads
  for (const bx of [L + 6, R - 6]) {                                              // wall brackets under the board
    p.set(bx, B + 1, IRON.h); p.set(bx, B + 2, IRON.m); p.set(bx + 1, B + 1, IRON.d);
  }
  for (const lx of BB_LAMPS) {                                                    // gooseneck lamps: plate, neck, shade
    const y = BB_LAMP_Y;
    p.set(lx - 1, T - 1, IRON.d); p.set(lx, T - 1, IRON.h); p.set(lx + 1, T - 1, IRON.d);
    for (let yy = y + 3; yy < T - 1; yy++) p.set(lx, yy, IRON.m);
    p.set(lx, y + 3, IRON.w);                                                     // the neck, warm where the lamp lights it
    p.set(lx - 1, y, IRON.l); p.set(lx, y, '#5a5460'); p.set(lx + 1, y, IRON.h);   // the hood, its top catching the moon
    for (let x = lx - 2; x <= lx + 2; x++) p.set(x, y + 1, x === lx - 2 ? IRON.l : x === lx + 2 ? IRON.d : IRON.h);
    for (let x = lx - 2; x <= lx + 2; x++) p.set(x, y + 2, Math.abs(x - lx) < 2 ? BB_LAMP.core : BB_LAMP.rim);   // lit opening
  }
  return trim(p);
}
/** the board's own ad, painted on white enamel and seen by lamplight at night (dim: the right-hand lamp is out) */
function renderAd(dim) {
  const { w, h } = BB, p = pix(w, h), lamps = BB_LAMPS.map(lx => lx - BB.x);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {                       // enamel with a scallop of light under each lamp
    let cone = 0;
    lamps.forEach((lx, k) => {
      const dx = (i - lx) / (4 + j * 0.7);
      cone = Math.max(cone, (dim && k === 2 ? 0.2 : 1) * Math.exp(-dx * dx) * Math.max(0, 1 - j / 14));
    });
    let v = 0.41 + 0.1 * (1 - j / h) + 0.16 * smooth(0.18, 0.3, cone) + 0.15 * smooth(0.5, 0.62, cone);   // the bottom rows sit on one step of the ramp: no dither line along the frame
    if (dim) v -= 0.12 * Math.exp(-(((i - lamps[2]) / 12) ** 2)) * (1 - j / (h + 10));
    p.set(i, j, pick(ENAMEL, v, i, j, 0.7));
  }
  for (const [cx, cy, sx, sy] of [[1, 1, 1, 1], [w - 2, 1, -1, 1], [1, h - 2, 1, -1], [w - 2, h - 2, -1, -1]]) {   // Delft-tile corners
    p.set(cx, cy, DELFT.m); p.set(cx + sx, cy, DELFT.l); p.set(cx, cy + sy, DELFT.l);
  }
  for (const [cx, cy] of [[0, 7], [0, 8], [41, 15], [17, 20], [18, 20], [29, 0], [41, 3]]) p.set(cx, cy, '#1a1c2a');   // chipped enamel
  const c = gradeAd(p.done(), { tint: BB_RUST, wear: 0.5, seed: 7 });             // rust runs down the enamel; the inks go on after it, so every letter stays whole
  p.x.fillStyle = DELFT.d;
  TYRES.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === 'd') p.x.fillRect(BIKE_AT.x + i, BIKE_AT.y + 6 + j, 1, 1); });
  text(p.x, 'FIETS', 21, 5, DELFT.d);
  text(p.x, 'HUREN', 21, 12, '#8a1a24');
  return c;
}

/* ================= Far quay: street, coping, brick wall, bollards, parked bikes ================= */
const BIKE = ['..11..11.', '...1..1..', '.1111111.', '1.1.1.1.1', '.1.....1.'];
const PARKED = [[44, '#2a3a5a'], [120, '#4a2a2a'], [148, '#1e1e26'], [198, '#3a4a3a'], [230, '#5a4a3a'], [388, '#2a2a3a'], [426, '#6a2a3a'], [454, '#1e2a2a']];
const onBridge = x => x >= BR.x0 - 2 && x <= BR.x1 + 2;
function renderQuay() {
  const p = pix(W, H), f = pix(W, H);
  for (let x = 0; x < W; x++) {
    if (x >= GAP.x0 && x < GAP.x1) continue;
    for (let y = BASE; y < WL; y++) {
      const lt = lampTerm(x, 178);
      let col;
      if (y <= 186) {                                                                     // cobbled street
        const c = hash(x >> 1, y, 5);
        col = pick(['#15141b', '#1c1a22', '#24212a', '#3a2e28', '#54402e', '#6e5236'], 0.18 + (y - BASE) * 0.07 + 0.7 * lt + (c - 0.5) * 0.12, x, y);
      } else if (y === 187) col = pick(['#3a3a44', '#55555e', '#74706c', '#968a74'], 0.35 + 0.65 * lt, x, y);
      else if (y === 188) col = '#26262e';
      else if (y === WL - 1) col = '#121a16';
      else col = (y === 190 && (x + (y >> 1)) % 6 === 0) ? '#160e0e' : pick(['#1e1212', '#2a1a18', '#3a241e'], 0.35 + 0.5 * lt + (hash(x >> 2, y, 6) - 0.5) * 0.3, x, y);
      p.set(x, y, col);
    }
  }
  // Amsterdammertjes: the little iron bollards along the quay edge
  for (let x = 5; x < W; x += 11) {
    if (onBridge(x) || (x >= GAP.x0 && x < GAP.x1) || TREES.some(t => Math.abs(x - t.x) < 4) || LAMPS.some(l => Math.abs(x - l) < 3)) continue;
    const lt = lampTerm(x, 180);
    f.set(x, 182, lt > 0.3 ? '#8a4a32' : '#5a2a22'); f.set(x, 183, '#3a1812'); f.set(x, 184, '#3a1812'); f.set(x, 185, '#2a100c'); f.set(x, 186, '#2a100c');
  }
  for (const [bx, col] of PARKED) {                                                       // bikes left against the bollards
    BIKE.forEach((row, j) => { for (let i = 0; i < 9; i++) if (row[i] === '1') f.set(bx + i, 182 + j, j < 2 && i > 5 ? '#6a6a74' : col); });
    f.set(bx + 7, 182, '#8a8a94');
  }
  return { back: trim(p), front: trim(f) };
}

/* ================= Elms on the far quay (lamp-lit undersides, pale seed clusters) =================
   The trunks and limbs are painted once and stand still. Each crown is kept whole and also cut into clumps of leaves:
   per frame the whole crown leans with the breeze, more towards its top, and the clumps ride over it, each a little
   out of step (drawElms), so the foliage stirs instead of sliding as one sprite. At rest they add up to the crown. */
const SHIMMER = [];                                 // leaf pixels that catch the light as the breeze moves them
const ELMS = [];                                    // per tree: its x, the whole crown, its clumps and its rustle: the
                                                    // leaves that turn their pale undersides up while a gust goes through
const LEAN = 2.2;                                   // px the top of a crown leans downwind at the height of a gust
/** how far right a leaf at (x, y) may lean before it reaches the ad board's frame, lamps or shadow */
const boardRoom = (x, y) => (x < BB.x && y >= BB.y - 10 && y <= BB.y + BB.h + 5 ? Math.max(0, BB.x - 3 - x) : 9);
function renderTrees() {
  const p = pix(W, H);
  for (const T of TREES) {
    const r = rng(300 + T.s), r2 = rng(900 + T.s), pts = [], rustle = [];
    // trunk and the first limbs
    for (let y = Math.round(T.cy + T.ry * 0.3); y < EDGE; y++) for (let k = -1; k <= 1; k++) {
      const lt = lampTerm(T.x + k, y);
      p.set(T.x + k, y, k < 0 ? pick(['#1e1812', '#3a2c20', '#5a4430'], 0.3 + lt, T.x + k, y) : k > 0 ? '#0c0a08' : '#18130e');
    }
    for (let k = 0; k < 9; k++) {
      p.set(T.x - 1 - Math.round(k * 0.9), Math.round(T.cy + T.ry * 0.3) - k, '#16120e');
      p.set(T.x + 1 + Math.round(k * 0.8), Math.round(T.cy + T.ry * 0.3) - k, '#100c0a');
    }
    for (let y = Math.floor(T.cy - T.ry - 3); y <= T.cy + T.ry + 2; y++) for (let x = Math.floor(T.x - T.rx - 4); x <= T.x + T.rx + 4; x++) {
      const dx = (x + 0.5 - T.x) / T.rx, dy = (y + 0.5 - T.cy) / T.ry, base = 1 - dx * dx - dy * dy;
      const n = vnoise(x / 5, y / 4, T.s * 7) * 0.55 + vnoise(x / 2.2, y / 2, T.s * 7 + 1) * 0.45;
      const v = base + (n - 0.5) * 0.9;
      if (v <= 0 || (v < 0.1 && bayer(x, y) > 0.5)) continue;
      if (base > 0.15 && base < 0.75 && n < 0.24) continue;                              // gaps in the crown
      const lit = clamp(dy * 0.85 + (n - 0.5) * 0.7 + 0.6 * lampTerm(x, y) - 0.1, 0, 1);
      let col = lit > 0.5 ? pick(LEAFLIT, (lit - 0.5) * 1.8 + (n - 0.5) * 0.4, x, y) : pick(LEAF, 0.2 + n * 0.55 + (0.3 - dy) * 0.12, x, y);
      if (dy < -0.4 && dx < 0 && n > 0.62) col = '#243436';                               // moonlit crown
      if (hash(x, y, T.s) < 0.012 && dy > -0.4) col = '#8a8c62';                          // seed clusters
      pts.push([x, y, col]);
      if (lit > 0.55 && r() < 0.035) SHIMMER.push({ x, y, k: 20 + Math.floor(r() * 40), ph: r() });
      if (r2() < 0.07) rustle.push({ x, y, k: 36 + Math.floor(r2() * 40), ph: r2(),
        col: lit > 0.5 ? LEAFLIT[4] : dy < -0.4 && dx < 0 ? '#3c5456' : LEAF[6] });
    }
    // the whole crown, with how much each of its rows leans (none where the limbs hold it, all of it at the top)
    const crown = cutout(pts), yb = T.cy + T.ry, rows = crown.c.height;
    crown.f = Array.from({ length: rows }, (_, j) => clamp((yb - crown.y - j) / (T.ry * 1.6), 0, 1));
    crown.cap = Array.from({ length: rows }, () => 9);
    for (const [x, y] of pts) crown.cap[y - crown.y] = Math.min(crown.cap[y - crown.y], boardRoom(x, y));
    // its clumps: every leaf goes to the nearest of a handful of centres (ragged borders), higher clumps move more
    const cen = Array.from({ length: 11 }, () => {
      const a = r2() * TAU, d = Math.sqrt(r2()) * 0.85;
      return { x: T.x + Math.cos(a) * d * T.rx, y: T.cy + Math.sin(a) * d * T.ry, pts: [] };
    });
    for (const pt of pts) {
      let best = cen[0], bd = 1e9;
      cen.forEach((c, i) => {
        const d = Math.hypot(pt[0] - c.x, (pt[1] - c.y) * 1.3) + vnoise(pt[0] / 2.5, pt[1] / 2.5, T.s * 31 + i) * 4;
        if (d < bd) { bd = d; best = c; }
      });
      best.pts.push(pt);
    }
    const clumps = cen.filter(c => c.pts.length).map(c => {
      const n = c.pts.length, cx = c.pts.reduce((s, q) => s + q[0], 0) / n, cy = c.pts.reduce((s, q) => s + q[1], 0) / n;
      return { ...cutout(c.pts), cx, amp: 0.45 + 0.55 * clamp((yb - cy) / (2 * T.ry), 0, 1), k: 24 + Math.floor(r2() * 24), ph: r2(),
        k2: 17 + Math.floor(r2() * 20), ph2: r2(), cap: Math.min(...c.pts.map(q => boardRoom(q[0], q[1] - 1)), ...c.pts.map(q => boardRoom(q[0], q[1] + 1))) };
    });
    for (const s of rustle) s.j = s.y - crown.y;
    rustle.sort((a, b) => (a.col < b.col ? -1 : a.col > b.col ? 1 : 0));                  // by colour: fewer fill changes per frame
    ELMS.push({ x: T.x, crown, clumps, rustle, k: 19 + T.s * 4, ph: T.s * 0.31 });
  }
  return trim(p);
}
/** a crown drawn in bands of rows that lean together: `lean` px at its top, less further down */
function drawCrown(o, lean) {
  const h = o.c.height, w = o.c.width, offAt = j => Math.min(o.cap[j], Math.round(lean * o.f[j]));
  for (let j = 0; j < h;) {
    const off = offAt(j);
    let j2 = j + 1;
    while (j2 < h && offAt(j2) === off) j2++;
    ctx.drawImage(o.c, 0, j, w, j2 - j, o.x + off, o.y + j, w, j2 - j);
    j = j2;
  }
}
function drawElms(t) {
  blit(TRUNKS_C);
  for (const E of ELMS) {
    const o = E.crown, g = breeze(t, E.x), lean = 0.8 * LEAN * g + 0.4 * wave(t, E.k, E.ph);
    drawCrown(o, lean);
    for (const c of E.clumps) {
      const dx = Math.min(c.cap, Math.round(c.amp * (LEAN * breeze(t, c.cx) + 0.7 * wave(t, c.k, c.ph))));
      ctx.drawImage(c.c, c.x + dx, c.y + Math.round(c.amp * 0.6 * wave(t, c.k2, c.ph2)));
    }
    if (g <= 0.52) continue;
    let col = null;
    for (const s of E.rustle) {                                                          // pale undersides, while it blows
      const a = (g - 0.5) * 2.4 * (0.5 + 0.5 * wave(t, s.k, s.ph));
      if (a < 0.06) continue;
      if (s.col !== col) ctx.fillStyle = col = s.col;
      ctx.globalAlpha = Math.min(0.8, a);
      ctx.fillRect(s.x + Math.min(o.cap[s.j], Math.round(lean * o.f[s.j])), s.y, 1, 1);
    }
    ctx.globalAlpha = 1;
  }
}

/* ================= Canal lamps (far quay and bridge ends) ================= */
function renderLamps() {
  const p = pix(W, H);
  for (const lx of LAMPS) {
    for (let y = 164; y < EDGE; y++) p.set(lx, y, y > 183 ? '#23252e' : '#15171e');
    p.set(lx - 1, 185, '#1a1c24'); p.set(lx + 1, 185, '#1a1c24'); p.set(lx - 1, 186, '#1a1c24'); p.set(lx + 1, 186, '#1a1c24');
    p.set(lx, 157, '#2a2c34');
    for (let x = lx - 2; x <= lx + 2; x++) p.set(x, 158, '#1a1c24');
    for (let x = lx - 1; x <= lx + 1; x++) { p.set(x, 159, '#22242c'); p.set(x, 163, '#1a1c24'); }
    for (let y = 160; y <= 162; y++) for (let x = lx - 1; x <= lx + 1; x++) p.set(x, y, x === lx ? '#fff8e0' : '#ffd890');
  }
  return trim(p);
}

/* ================= The hump-backed bridge over the side canal ================= */
const BULBS = [];                                   // strings of little warm bulbs: arch outline and railing
function renderBridge() {
  const b = pix(W, H), rl = pix(W, H);
  for (let x = BR.x0; x < BR.x1; x++) {
    const top = Math.round(deckY(x)), ay = archY(x);
    for (let y = top; y < WL; y++) {
      let col;
      if (y === top) col = pick(['#5a5864', '#7a7682', '#a09488'], 0.5 + 0.3 * (1 - Math.abs(x - BR.cx) / 60), x, y);
      else if (y === top + 1) col = '#34323c';
      else if (ay !== null && y >= ay) {                                                   // through the arch: the side canal
        col = y > 188 ? '#0a0e1c' : '#070a16';
        if (x === BR.cx + 7 && y >= 186) col = y === 186 ? '#d8a860' : '#4a3a2e';
        if (x === BR.cx - 6 && y >= 187) col = y === 187 ? '#b88a50' : '#3a2e28';
      } else {
        const u = (x + 0.5 - BR.cx) / (BR.ax + 2.2), ringY = WL - 1 - (WL - 1 - BR.crown + 2.2) * Math.sqrt(Math.max(0, 1 - u * u));
        if (Math.abs(u) < 1 && y >= ringY - 0.5) {                                          // stone arch ring (voussoirs)
          const ang = Math.atan2(WL - 1 - y, x + 0.5 - BR.cx);
          col = (Math.floor(ang * 5.1) & 1) ? '#8a8490' : '#6a6672';
          if (ay !== null && y >= ay - 1.2) col = '#4a4652';
        } else col = y === WL - 1 ? '#141c16' : (y % 3 === 0 || (x + (Math.floor(y / 3) & 1) * 3) % 6 === 0) ? '#241412' : pick(['#301a16', '#3e221c', '#4c2c22'], 0.4 + 0.3 * lampTerm(x, y), x, y);
      }
      b.set(x, y, col);
    }
  }
  // bikes chained to the railing, then the railing itself
  for (const [bx, col] of [[259, '#3a3a4a'], [338, '#2a3a4a'], [347, '#6a2a2a']]) {
    const by = Math.round(deckY(bx + 4)) - 5;
    BIKE.forEach((row, j) => { for (let i = 0; i < 9; i++) if (row[i] === '1') rl.set(bx + i, by + j, j < 2 && i > 5 ? '#7a7a84' : col); });
  }
  for (let x = BR.x0 + 2; x < BR.x1 - 2; x++) {
    const top = Math.round(deckY(x)), rt = top - 6;
    rl.set(x, rt, '#44464f'); rl.set(x, rt + 1, '#23252c');
    if (x % 3 === 0) for (let y = rt + 2; y < top; y++) rl.set(x, y, '#1c1e25');
    rl.set(x, top - 1, '#2a2c33');
  }
  for (const px of [BR.x0 + 2, BR.x1 - 3]) for (let y = Math.round(deckY(px)) - 8; y < Math.round(deckY(px)); y++) { rl.set(px, y, '#2a2c34'); rl.set(px + 1, y, '#16171d'); }
  // bulbs: along the arch ring, evenly spaced, and along the top rail
  const pts = [];
  for (let k = 0; k <= 400; k++) {
    const th = Math.PI * k / 400;
    pts.push([BR.cx + (BR.ax + 2.4) * Math.cos(th), WL - 1 - (WL - 1 - BR.crown + 2.4) * Math.sin(th)]);
  }
  let acc = 0;
  for (let k = 1; k < pts.length; k++) {
    acc += Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]);
    if (acc >= 3.1) { acc = 0; BULBS.push({ x: Math.round(pts[k][0]), y: Math.round(pts[k][1]), arch: true }); }
  }
  for (let x = BR.x0 + 5; x < BR.x1 - 4; x += 4) BULBS.push({ x, y: Math.round(deckY(x)) - 7, arch: false });
  BULBS.forEach((u, i) => { u.k = 3 + (i * 7) % 11; u.ph = hash(i, 3); u.c = i % 3; });
  return { body: trim(b), rail: trim(rl) };
}

/* ================= The canal ================= */
function renderWater() {
  const h = NEARQ - WL, p = pix(W, h);
  const P = ['#131c36', '#101830', '#0d1429', '#0a1022', '#080d1c', '#070b18'];
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) p.set(x, y, pick(P, y / h + (vnoise(x / 40, y / 3, 9) - 0.5) * 0.12, x, y));
  return p.done();
}

/* ================= The houseboat: steel hull, green cabin, roof garden, lit porthole ================= */
const HB_WINS = [{ x: 12, kind: 'plant' }, { x: 36, kind: 'lamp' }, { x: 60, kind: 'room' }, { x: 84, kind: 'dark' }, { x: 108, kind: 'curtain' }];
const HB_CURT = HB_WINS.find(w => w.kind === 'curtain').x;   // the window left open: its curtains move (drawHouseboat)
const PORT = [{ x: 58, y: 240, lit: false }, { x: 120, y: 240, lit: true }];
const hbBow = y => HB.x1 - Math.round(Math.pow(clamp((y - HB.gun) / (HB.wl - HB.gun), 0, 1), 1.6) * 11);
function renderHouseboat() {
  const p = pix(W, H);
  // hull
  for (let y = HB.gun; y < HB.wl; y++) for (let x = 0; x <= hbBow(y); x++) {
    let col;
    if (y === HB.gun) col = x > hbBow(y) - 2 ? '#8a8e98' : '#50545f';
    else if (y === HB.gun + 1) col = '#2a2e38';
    else if (y === HB.gun + 4) col = x % 9 === 0 ? '#3a3e48' : '#62666f';
    else if (y >= HB.wl - 2) col = '#3a1614';
    else col = pick(['#0b0d13', '#10131a', '#161a22', '#1e222c'], 0.55 - (y - HB.gun) * 0.03 + 0.25 * Math.exp(-Math.hypot((x - 120) / 20, (y - 240) / 8)), x, y);
    if (x === hbBow(y)) col = '#2a2e38';
    p.set(x, y, col);
  }
  for (const pt of PORT) for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
    const d = Math.hypot(dx, dy);
    if (d > 3.2) continue;
    p.set(pt.x + dx, pt.y + dy, d > 2.1 ? (dx + dy < 0 ? '#c8a454' : '#7a5a2a') : pt.lit ? (dy < 0 ? '#ffe2a0' : '#f4b060') : (dx + dy < -1 ? '#3a4a6a' : '#141a2a'));
  }
  for (const fx of [28, 90, 150]) for (let dy = 0; dy < 6; dy++) for (let dx = 0; dx < 4; dx++) {   // old tyres as fenders
    const ring = dx === 0 || dx === 3 || dy === 0 || dy === 5;
    if (ring && !((dx === 0 || dx === 3) && (dy === 0 || dy === 5))) p.set(fx + dx, HB.gun + 2 + dy, dx === 0 ? '#2a2a30' : '#0a0a0c');
  }
  // cabin
  for (let y = HB.roof; y < HB.gun; y++) for (let x = 5; x <= 142; x++) {
    let col;
    if (y === HB.roof) col = x < 60 ? '#8a7454' : '#7a664a';
    else if (y === HB.roof + 1) col = '#3a3024';
    else if (y === HB.gun - 1) col = '#2e2a26';
    else {
      const warm = Math.max(...HB_WINS.map(w => w.kind === 'dark' ? 0 : Math.exp(-Math.hypot((x - w.x - 5) / 10, (y - 219) / 9))));
      col = (x % 3 === 0) ? pick(['#101c16', '#1a2a20', '#3a3424'], 0.3 + warm * 0.8, x, y) : pick(['#16261e', '#1e3228', '#2c3e2c', '#4a4a30'], 0.3 + warm * 0.75, x, y);
    }
    if (x === 5) col = '#0c1410'; else if (x === 142) col = '#2a3a30';
    p.set(x, y, col);
  }
  for (const w of HB_WINS) for (let y = 213; y <= 225; y++) for (let x = w.x - 1; x <= w.x + 10; x++) {
    const frame = x === w.x - 1 || x === w.x + 10 || y === 213 || y === 225;
    if (frame) { p.set(x, y, y === 225 ? '#8a8478' : '#c8c0b0'); continue; }
    const f = (y - 214) / 10, i = x - w.x;
    let col = f < 0.3 ? '#ffe4a8' : f < 0.75 ? '#f6bc6c' : '#dc9448';
    if (w.kind === 'dark') col = f < 0.2 ? '#1c2640' : '#0c1222';
    else if (w.kind === 'plant' && f > 0.5 && (i + y) % 3 !== 0) col = f > 0.8 ? '#6a3a20' : '#2a4a22';
    else if (w.kind === 'lamp' && Math.hypot(i - 6, y - 216) < 1.6) col = '#ffffff';
    else if (w.kind === 'lamp' && i >= 5 && i <= 7 && y >= 219 && y <= 222) col = i === 6 ? '#6a4a2a' : col;
    else if (w.kind === 'curtain' && (i < 3 || i > 6)) col = i === 1 || i === 8 ? '#b06a3a' : '#d08848';
    else if (w.kind === 'room' && y >= 221 && i > 1) col = i % 3 ? '#8a5028' : '#6a3a1e';              // sofa back
    if (y === 219 && w.kind !== 'dark') col = w.kind === 'curtain' && (i < 3 || i > 6) ? col : '#e8c88c';
    p.set(x, y, col);
  }
  for (let y = 211; y < HB.gun; y++) for (let x = 126; x <= 134; x++) {                    // door with a round window
    const d = Math.hypot(x - 130, y - 216);
    p.set(x, y, d < 2.3 ? '#ffd890' : x === 126 || x === 134 || y === 211 ? '#2a1a10' : (x === 127 ? '#6a4228' : '#4a2e1c'));
  }
  p.set(133, 222, '#e0b050');
  for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {                      // lifebuoy
    const d = Math.hypot(dx, dy);
    if (d > 3.3 || d < 1.6) continue;
    p.set(146 + dx, 219 + dy, (Math.floor(Math.atan2(dy, dx) / (Math.PI / 2) + 4) & 1) ? '#e8e0d4' : '#c8302a');
  }
  for (let x = 143; x < 170; x++) p.set(x, HB.gun - 1, x % 4 === 0 ? '#3a3a44' : '#26262e');   // bow deck
  for (let y = HB.gun - 6; y < HB.gun - 1; y++) for (const x of [150, 162]) p.set(x, y, '#3a3a44');
  for (let x = 150; x <= 170; x++) p.set(x, HB.gun - 6, '#4a4a54');
  // pots of herbs on the bow deck
  for (const [px0, col] of [[152, '#8a4a2a'], [158, '#5a6a7a'], [164, '#8a4a2a']]) {
    for (let y = HB.gun - 4; y < HB.gun - 1; y++) for (let x = px0; x < px0 + 4; x++) p.set(x, y, y === HB.gun - 4 ? mixHex(col, '#ffffff', 0.25) : col);
    for (let k = 0; k < 7; k++) p.set(px0 + (k * 5) % 4, HB.gun - 5 - (k % 3), k % 2 ? '#3a6a2a' : '#2a4e22');
  }
  // stovepipe on the roof
  for (let y = 195; y < HB.roof; y++) { p.set(30, y, '#16161a'); p.set(31, y, y % 4 === 0 ? '#3a3a42' : '#0c0c10'); }
  for (let x = 28; x <= 33; x++) p.set(x, 193, '#1e1e24'); for (let x = 29; x <= 32; x++) p.set(x, 194, '#101014');
  // mooring ropes to the near quay
  for (const [ax, ay, bx2, by2] of [[20, 243, 6, NEARQ], [162, 243, 180, NEARQ]]) {
    const n = Math.max(Math.abs(bx2 - ax), Math.abs(by2 - ay));
    for (let k = 0; k <= n; k++) { const u = k / n; p.set(Math.round(lerp(ax, bx2, u)), Math.round(lerp(ay, by2, u) + Math.sin(u * Math.PI) * 2), '#2e2820'); }
  }
  return trim(p);
}
/** the roof garden (drawn with a gentle sway): a leafy fig, geraniums, lavender, a little olive tree, a chair */
function renderRoofPlants() {
  const p = pix(W, H), r = rng(88);
  const pot = (x0, w, h, col) => { for (let y = HB.roof - h; y < HB.roof; y++) for (let x = x0; x < x0 + w; x++) p.set(x, y, y === HB.roof - h ? mixHex(col, '#ffe0c0', 0.3) : x === x0 ? mixHex(col, '#ffffff', 0.12) : col); };
  // fig in a big pot
  pot(9, 12, 6, '#8a4a2a');
  for (let k = 0; k < 60; k++) {                                                          // big fig leaves, 2x2 clusters
    const a = r() * TAU, d = Math.sqrt(r()), x = Math.round(15 + Math.cos(a) * d * 9), y = Math.round(193 + Math.sin(a) * d * 7);
    const lit = y > 194 && x > 12, col = lit ? (r() < 0.5 ? '#3e5e26' : '#5a7a30') : (r() < 0.5 ? '#16281a' : '#22381c');
    p.set(x, y, col); p.set(x + 1, y, col); p.set(x, y + 1, col); if (r() < 0.5) p.set(x + 1, y + 1, lit ? '#6e8a3a' : '#2c4422');
  }
  for (let y = 196; y < HB.roof - 6; y++) p.set(15, y, '#3a2a1a');
  // geraniums in a box
  pot(42, 14, 4, '#6a4a30');
  for (let k = 0; k < 40; k++) { const x = 42 + Math.floor(r() * 14), y = 199 + Math.floor(r() * 5); p.set(x, y, r() < 0.35 ? (r() < 0.5 ? '#e0404a' : '#ff6a70') : '#2a4a22'); }
  // lavender
  pot(60, 6, 5, '#5a6a7a');
  for (let k = 0; k < 6; k++) for (let y = 0; y < 5 + (k % 3); y++) p.set(60 + k, HB.roof - 6 - y, y > 2 ? '#8a6ab8' : '#3a5a3a');
  // folding chair
  for (let y = 199; y < HB.roof; y++) { p.set(113, y, '#5a4a3a'); if (y > 202) p.set(119, y, '#4a3a2e'); }
  for (let x = 113; x <= 119; x++) p.set(x, 203, '#7a6448');
  // little olive tree in a pot
  pot(126, 10, 6, '#5a6a7e');
  for (let y = 193; y < HB.roof - 6; y++) p.set(131, y, '#4a3a2a');
  p.set(130, 196, '#4a3a2a'); p.set(129, 195, '#4a3a2a'); p.set(132, 194, '#4a3a2a'); p.set(133, 193, '#4a3a2a');
  for (let k = 0; k < 130; k++) {
    const a = r() * TAU, d = Math.sqrt(r()), x = Math.round(131 + Math.cos(a) * d * 8), y = Math.round(188 + Math.sin(a) * d * 6);
    p.set(x, y, r() < 0.25 ? '#8a9a7a' : r() < 0.6 ? '#5a6a52' : '#3a4a38');
  }
  return trim(p);
}

/* ================= The grey heron (facing right; feet on the houseboat roof) ================= */
const HERON_KEY = { K: '#101016', W: '#d0d4de', w: '#98a0b2', G: '#7a8298', g: '#5c6276', D: '#33374a', Y: '#e0a83a',
  y: '#9a6a24', L: '#8a7446', S: '#b4b8c8', P: '#8e94a8' };
const H_HEAD = ['..........WWW.......', '.........KKKWW......', '.......KK.WWKWYYY...', '......K....WWWYYYYY.', '...........Wwy......'];
const H_HEADL = ['........WWW.........', '.......WWKKK........', '....YYYWKWW.KK......', '..YYYYYWWW....K.....', '........yWWw........'];
const H_NECK = ['...........wW.......', '..........wW........', '..........wK........', '..........wW........', '..........wK........',
  '...........wW.......', '...........wWS......'];
const H_BODY = ['..........GwWSS.....', '.........GGGwSS.....', '.......GGGGKKSSP....', '.....GGGGGGGGSSP....', '...gGGGGGGGGGSSP....',
  '.DDgGGGGGGGGGSP.....', 'DDDDgGGGGGGGGS......', '.DDDDgGGGGGGS.......', '...DDDDgGGGS........', '......DDDD..........'];
const H_LEGS = ['..........LL........', '..........L.L.......', '..........L.L.......', '..........L.L.......', '..........L.L.......',
  '..........L..L......', '..........L..L......', '..........L..L......', '.........L...L......', '.........L...L......',
  '.........L...L......', '........LLL.LLL.....'];
const H_ONELEG = ['..........LL........', '..........L.LL......', '..........L..L......', '..........L.........', '..........L.........',
  '..........L.........', '..........L.........', '..........L.........', '..........L.........', '..........L.........',
  '..........L.........', '.........LLL........'];
const BLANK = n => Array.from({ length: n }, () => '....................');
const HERON_POSES = {
  stand: [...H_HEAD, ...H_NECK, ...H_BODY, ...H_LEGS],
  look: [...H_HEADL, ...H_NECK, ...H_BODY, ...H_LEGS],
  alert: [...H_HEAD.map(r => '.' + r.slice(0, 19)), '............wW......', '............wW......', '............wK......', '............wW......',
    '...........wK........'.slice(0, 20), '...........wW.......', '...........wW.......', '...........wK.......', '...........wWS......', ...H_BODY, ...H_LEGS],
  hunch: [...BLANK(6), '..........WWW.......', '.........KKKWW......', '.......KK.WWKWYYY...', '......K....WWWYYYY..', '..........GWWwy.....',
    '.........GGwWS......', ...H_BODY, ...H_LEGS],
  preen: [...BLANK(5), '..........wWW.......', '..........wWWW......', '...........wWWW.....', '.............WW.....', '...........KKWWK....',
    '............WWWW....', '............WYYW....', '..........GwWSYS....', '.........GGGwSYS....', ...H_BODY.slice(2), ...H_LEGS],
  preen2: [...BLANK(6), '..........wWW.......', '..........wWWW......', '...........wWWW.....', '.............WW.....', '...........KKWWK....',
    '............WWWW....', '..........GwWWYS....', '.........GGGwSYy....', ...H_BODY.slice(2), ...H_LEGS],
  oneleg: [...H_HEAD, ...H_NECK, ...H_BODY, ...H_ONELEG],
};

/* ================= The little open boat (bow to the left): friends, a skipper and a lantern ================= */
const BOAT_W = 62, BOAT_H = 19, LANT = { x: 55, y: 3 };   // sprite size, lantern position in the sprite
function renderBoat() {
  const p = pix(BOAT_W, BOAT_H), wl = BOAT_H - 1, lit = x => clamp(1 - Math.abs(x - LANT.x) / 40, 0, 1);
  for (let y = 11; y <= wl; y++) {
    const x0 = [4, 2, 1, 1, 2, 3, 5, 8][y - 11], x1 = [57, 58, 58, 58, 58, 57, 56, 54][y - 11];
    for (let x = x0; x <= x1; x++) {
      let col;
      if (y === 11) col = mixHex('#6a4a30', '#d8a060', lit(x) * 0.8);
      else if (y === 12) col = mixHex('#2a3a50', '#5a5a60', lit(x) * 0.4);
      else col = ['#1e2c40', '#1a2638', '#16202f', '#121a27', '#0e1520', '#0a0f18'][Math.min(5, y - 13)];
      if (y === 14 && x > x0 + 1 && x < x1 - 1) col = '#3a4660';
      p.set(x, y, col);
    }
  }
  p.set(2, 10, '#6a4a30'); p.set(3, 10, '#7a5638'); p.set(1, 9, '#5a3e28');                          // raked bow
  for (let y = 9; y <= 15; y++) for (let x = 59; x <= 60; x++) p.set(x, y, y === 9 ? '#55555e' : x === 59 ? '#2a2a32' : '#1a1a20');   // outboard
  p.set(61, 15, '#1a1a20'); p.set(58, 10, '#3a3a42'); p.set(57, 9, '#3a3a42');                     // tiller
  const people = [[8, 4, '#3a4a62', '#c89a60'], [15, 3, '#6a3434', '#2a1a14'], [23, 4, '#3e5a40', '#1a1210'],
    [31, 3, '#5a4a70', '#6a4428'], [38, 4, '#6a5a3e', '#e0c080'], [50, 2, '#2a2e3a', '#3a2a1c']];
  for (const [hx, hy, coat, hair] of people) {
    const L = lit(hx) * 0.55;
    p.set(hx, hy, hair); p.set(hx + 1, hy, hair); p.set(hx, hy + 1, hair); p.set(hx + 1, hy + 1, mixHex('#9a6a4a', '#f4c090', L + 0.3));
    for (let y = hy + 2; y <= 11; y++) for (let x = hx - 1; x <= hx + 2; x++) {
      if (y === hy + 2 && (x === hx - 1 || x === hx + 2)) continue;                                   // rounded shoulders
      p.set(x, y, x === hx + 2 || (y === hy + 2 && x === hx + 1) ? mixHex(coat, '#ffc880', 0.2 + L) : x === hx - 1 ? mixHex(coat, '#05060a', 0.35) : coat);
    }
  }
  p.set(28, 8, '#3a7a4a'); p.set(28, 9, '#2a5a3a'); p.set(28, 10, '#2a5a3a'); p.set(28, 7, '#9ad0a8');        // a bottle
  p.set(20, 9, '#e8e0c8'); p.set(35, 9, '#e8e0c8');                                                        // glasses
  for (let y = 0; y <= 10; y++) p.set(LANT.x, y, '#3a3026');                                               // lantern on its pole
  for (let x = LANT.x - 1; x <= LANT.x + 1; x++) { p.set(x, 1, '#2a2420'); p.set(x, 6, '#2a2420'); }
  for (let y = 2; y <= 5; y++) { p.set(LANT.x - 1, y, '#ffd890'); p.set(LANT.x, y, y < 4 ? '#fff6d8' : '#ffe0a0'); p.set(LANT.x + 1, y, '#f0b860'); }
  p.set(LANT.x, 0, '#4a3e30');
  return p.done();
}

/* ================= Cyclists (facing right; wheel bottom on the last row) ================= */
const RING = [[1, 9], [2, 9], [0, 10], [3, 10], [0, 11], [3, 11], [1, 12], [2, 12]];
function makeRider(rd, fr) {
  const cargo = rd.kind === 2, w = cargo ? 20 : 12, p = pix(w, 13);
  const F = '#2e303a', skin = '#e0a882', pants = '#1c1e2a', coatL = mixHex(rd.coat, '#ffe0b0', 0.3), coatD = mixHex(rd.coat, '#000000', 0.35);
  const fw = cargo ? 16 : 8;
  for (const [x, y] of RING) { p.set(x, y, F); p.set(fw + x, y, F); }
  for (const [x, y] of [[4, 10], [5, 10], [4, 9], [4, 8], [5, 8], [6, 8], [7, 8], [6, 9], [7, 9], [1, 8], [2, 8]]) p.set(x, y, F);
  p.set(3, 7, '#101014'); p.set(4, 7, '#101014');
  if (!cargo) {
    p.set(8, 8, F); p.set(8, 7, F); p.set(9, 9, F); p.set(9, 10, F); p.set(8, 6, F); p.set(9, 6, '#5a5c66');
    if (rd.kind === 1) { p.fill(9, 7, 2, 2, '#6a4a2a'); p.set(9, 7, '#8a6a44'); }                                // front basket
  } else {
    for (let x = 8; x <= 17; x++) p.set(x, 11, F);
    p.set(17, 10, F); p.set(17, 9, F); p.set(8, 7, F); p.set(8, 6, F); p.set(9, 6, '#5a5c66');
    for (let y = 6; y <= 10; y++) for (let x = 10; x <= 16; x++) p.set(x, y, y === 6 ? '#9a7a50' : x === 10 || x === 16 ? '#5a3e24' : (x + y) % 5 ? '#6e4e2e' : '#5e4226');
    p.set(13, 4, '#e0c080'); p.set(14, 4, '#e0c080'); p.set(13, 5, '#e0a882'); p.set(14, 5, '#e0a882');           // a child in the box
  }
  p.set(4, 6, pants); p.set(5, 6, pants);
  for (let y = 3; y <= 5; y++) { p.set(4, y, rd.coat); p.set(5, y, y === 3 ? coatL : rd.coat); }
  p.set(3, 4, coatD); p.set(3, 5, coatD);
  p.set(6, 4, rd.coat); p.set(7, 5, rd.coat); p.set(8, 5, skin);
  p.set(4, 1, rd.hair); p.set(5, 1, rd.hair); p.set(4, 2, rd.hair); p.set(5, 2, skin); p.set(4, 0, rd.hair);
  const near = fr ? [[5, 7], [5, 8], [4, 9]] : [[6, 7], [6, 8], [6, 9]], far = fr ? [[6, 7], [6, 8], [6, 9]] : [[5, 7], [5, 8], [4, 9]];
  for (const [x, y] of far) p.set(x, y, '#12131c');
  for (const [x, y] of near) p.set(x, y, pants);
  p.set(0, 8, '#ff3a30');
  p.set(cargo ? 16 : 10, cargo ? 8 : 7, '#fff4d0');
  return p.done();
}
const flipC = src => { const [c, x] = makeCanvas(src.width, src.height); x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0); return c; };

/* ================= Ducks and a coot, elm seeds ================= */
const DUCK_KEY = { g: '#1c4a30', G: '#2e6a44', y: '#c8a040', B: '#6a6a70', b: '#3a3a44', W: '#e8e8ec', t: '#2a2a30', h: '#5a4230', H: '#7a5a3a', k: '#141418', s: '#f0f0f0' };
const DUCKS = {
  drake: ['....gG.', '....gGy', 'tBBBWb.', '.bbbbb.'],
  hen: ['....hH.', '....hHy', 'hHhHhh.', '.hbhbh.'],
  coot: ['....kk.', '....kks', '.kkkkk.', '.kkkkk.'],
};
const SEED_KEY = { a: '#98a06e', b: '#b8c08c', c: '#7e8658', e: '#8a9264' };
const NEAR_KEY = { a: '#a8aa7a', b: '#c6c69a', c: '#a0a070', e: '#8e9068' };
const SEED_NEAR = [['.aaa.', 'abbba', 'abcba', 'abbba', '.aaa.'], ['.a.', 'aba', 'aca', 'aba', '.a.'], ['e', 'b', 'c', 'e']];
const SEED_MID = [['ab', 'ba'], ['b', 'a']];

/* ================= Mist on the water (tileable, dithered) ================= */
function makeMist(seed, h, maxA) {
  const p = pix(W, h), A = C('#8a94b6'), B = C('#a4a2b6');
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.9)), 1.8);
    const nz = 0.5 + 0.3 * Math.sin(TAU * 2 * x / W + seed + y * 0.07) + 0.18 * Math.sin(TAU * 5 * x / W - y * 0.21 + seed * 2) + 0.1 * Math.sin(TAU * 13 * x / W + y * 0.5);
    const q = Math.floor(clamp(prof * nz, 0, 1) * 4 + bayer(x, y) - 0.15) / 4;
    if (q <= 0) continue;
    p.set(x, y, [0, 1, 2].map(i => Math.round(lerp(A[i], B[i], v))), Math.round(Math.min(1, q) * maxA * 255));
  }
  return p.done();
}

/* ================= Foreground: an elm branch across the top-right corner, the near quay ================= */
function renderBranch() {
  const p = pix(W, 70), r = rng(515), leaves = [];
  const limb = (x0, y0, ang, len, wd, depth) => {
    let xx = x0, yy = y0, a = ang;
    for (let s = 0; s < len; s++) {
      a += (r() - 0.5) * 0.2 - 0.004;
      xx += Math.cos(a); yy += Math.sin(a);
      const w = Math.max(1, Math.round(wd * (1 - s / len * 0.75)));
      for (let k = 0; k < w; k++) p.set(Math.round(xx), Math.round(yy) + k, k === 0 && w > 1 ? '#1e2226' : '#0c0e10');
      if (depth < 3 && s > 4 && r() < 0.09) limb(xx, yy, a + (r() < 0.55 ? 0.55 : -0.5) + (r() - 0.5) * 0.3, len * (0.3 + r() * 0.3), w, depth + 1);
      if (s > len * 0.25 && r() < 0.3) leaves.push([xx + (r() - 0.5) * 9, yy + 1 + r() * 7]);
    }
    for (let k = 0; k < 6; k++) leaves.push([xx + (r() - 0.5) * 10, yy + (r() - 0.3) * 8]);
  };
  limb(W + 3, 4, Math.PI + 0.1, 118, 4, 0);
  limb(W + 3, -2, Math.PI - 0.05, 60, 2, 1);
  for (const [lx, ly] of leaves) {
    const X = Math.round(lx), Y = Math.round(ly), k = r();
    const col = k < 0.45 ? '#0a120c' : k < 0.8 ? '#101c12' : '#1a2a1a';
    p.set(X, Y, col); p.set(X + 1, Y, col); p.set(X - 1, Y, col); p.set(X, Y + 1, col); p.set(X + 1, Y + 1, col);
    if (r() < 0.3) p.set(X, Y - 1, '#1c2c22');
    if (r() < 0.05) { p.set(X + 1, Y + 1, '#4a4e3a'); p.set(X + 2, Y + 1, '#5e6248'); }                     // seed clusters
  }
  return trim(p);
}
function renderNearQuay() {
  const p = pix(W, H);
  for (let y = NEARQ; y < H; y++) for (let x = 0; x < W; x++) {
    let col;
    if (y === NEARQ) col = hash(x >> 2, 3) < 0.2 ? '#6a6a76' : '#4a4a56';
    else if (y === NEARQ + 1) col = '#2e2e38';
    else col = ((x + ((y - NEARQ) >> 2) * 7) % 23 === 0 || (y - NEARQ) % 4 === 1) ? '#101016' : pick(['#16161e', '#1c1c25', '#23232d'], hash(Math.floor((x + ((y - NEARQ) >> 2) * 7) / 23), y >> 2, 4), x, y);
    p.set(x, y, col);
  }
  for (const [ax, ay] of [[6, NEARQ + 2], [180, NEARQ + 2]]) for (const [dx, dy] of [[0, 0], [1, 0], [-1, 1], [2, 1], [-1, 2], [2, 2], [0, 3], [1, 3]]) p.set(ax + dx, ay + dy, '#4a4a54');
  return trim(p);
}
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.7, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.46) / (H / 2) * 0.92));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [6, 8, 18], Math.round(q * 0.42 * 255));
  }
  return p.done();
}

/* ================= Build-time particle and life tables ================= */
const SEEDS = [[], [], []];                         // elm seeds: far (behind the tower), mid (over the canal), near
{
  const r = rng(4747);
  [[70, 3, 5, 9, 16], [40, 5, 7, 14, 30], [12, 9, 13, 20, 54]].forEach(([count, nMin, nMax, amp, drift], layer) => {
    for (let i = 0; i < count; i++) SEEDS[layer].push({ id: layer * 100 + i, layer, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
      ks: 12 + Math.floor(r() * 18), p2: r(), kf: 24 + Math.floor(r() * 50), p4: r(), amp: amp * (0.6 + r() * 0.8),
      drift: drift * (0.5 + r()), land: r(), tone: r() });
  });
}
const FLOATERS = [];                                // seeds already floating on the canal
{ const r = rng(4848); for (let i = 0; i < 22; i++) FLOATERS.push({ x: r(), y: WL + 5 + Math.floor(r() * (NEARQ - WL - 10)), k: r() < 0.5 ? 1 : 2, ph: r() }); }
const MOTHS = [];                                   // two or three round each canal lamp (the bat hunts the bridge lamp's);
{                                                   // the cafe lamp's keep above its lantern, clear of the fascia's lettering
  const r = rng(5151);
  for (const lx of LAMPS) for (let j = 0; j < (lx === LAMPS[2] ? 3 : 2); j++) MOTHS.push({ x: lx, y: lx === LAMPS[1] ? 156 : 160,
    kx: 29 + Math.floor(r() * 26), px: r(), ky: 37 + Math.floor(r() * 30), py: r(), rx: 3 + r() * 4, ry: 2 + r() * 2.5, kw: 150 + Math.floor(r() * 70) });
}
const BIRDS = [
  { kind: 'drake', cx: 336, cy: 213, ax: 66, ay: 5, kx: 2, ky: 3, px: 0.1, py: 0.3 },
  { kind: 'hen', cx: 336, cy: 216, ax: 64, ay: 5, kx: 2, ky: 3, px: 0.075, py: 0.28 },
  { kind: 'coot', cx: 222, cy: 240, ax: 34, ay: 4, kx: 1, ky: 2, px: 0.6, py: 0.1 },
];
const STREAKS = [
  ...LAMPS.map((x, i) => ({ x, w: 3, col: '#ffd890', a: 0.42, seed: 1 + i })),
  { x: CAFE.x0 + 8, w: 7, col: '#ffb860', a: 0.26, seed: 7 }, { x: CAFE.x0 + 24, w: 6, col: '#ffb860', a: 0.24, seed: 8 },
];
// a picked weather (site/js/engine/weather.js): Amsterdam's own elm seeds, and the ones already floating on the canal, show
// only with its own weather. Far drops fall in front of the houses and end behind the quay street, mid ones land on the
// canal, near ones on the near quay's paving. The canal lamps, the bridge bulbs and the cafe light the far and mid drops
// (and the board's lamps, with the board); the near ones only catch the houseboat's windows, the lamps being across the
// water. One cfg per depth (far, mid, near)
const WEATHER = weatherPlans(['seeds']);
const WX_LAND = [[BASE, EDGE], [WL + 2, NEARQ - 2], [NEARQ + 2, H - 1]];
const WX_QUAY = [
  ...LAMPS.map(x => ({ x, y: 161, rx: 14, ry: 17, col: '#ffd890' })),
  { x: BR.cx, y: 180, rx: 56, ry: 13, col: '#ffc870' }, { x: CAFE.x0 + 17, y: 175, rx: 20, ry: 10, col: '#ffb860' },
];
const WX_HOUSEBOAT = [
  ...HB_WINS.filter(w => w.kind !== 'dark').map(w => ({ x: w.x + 5, y: 219, rx: 9, ry: 8, col: '#ffb860' })),
  ...PORT.filter(p => p.lit).map(p => ({ x: p.x, y: p.y, rx: 7, ry: 6, col: '#ffb860' })),
];
const wxDepths = far => [far, WX_QUAY, WX_HOUSEBOAT].map(lights => ({ land: WX_LAND, lights }));
const WX = wxDepths(WX_QUAY);
const WX_BB = wxDepths([...WX_QUAY, { x: BB.x + (BB.w >> 1), y: BB.y - 1, rx: 22, ry: 9, col: '#c89a5c' }]);   // + the board's lamps (small and dim)

/* ================= Prerendered state ================= */
let HL, SKY_C, MOON_C, WISPS, TOWER, ROW, QUAY, TRUNKS_C, LAMPS_C, BRIDGE, WATER_C, HBOAT, PLANTS, HERON_SPR, BOAT_C,
  RIDER_SPR, DUCK_SPR, SEED_SPR, MIST, BRANCH_C, NEARQ_C, VIG_C, PUFF, BOARD_C, ADS, BIKE_SPR, PITCH, LOOKERS;
function prerender() {
  HL = {
    moon: makeHalo(42, '#8ea0d8', 0.17, 1.7), moonCore: makeHalo(12, '#f0ecd8', 0.28, 1.5),
    tower: makeHalo(56, '#ffb458', 0.12, 1.6), towerCore: makeHalo(22, '#ffd89a', 0.14, 1.4), crown: makeHalo(15, '#6a90ff', 0.24, 1.4),
    flood: makeHalo(30, '#ffb458', 0.1, 1.6),                                             // floodlights at the tower's foot
    belfry: makeHalo(18, '#ffc870', 0.42, 1.3), ring: makeHalo(4, '#ffe0a0', 0.5, 1),
    win: makeHalo(7, '#ffb860', 0.13, 1.4), tv: makeHalo(9, '#7a9aff', 0.26), cafe: makeHalo(26, '#ffa850', 0.2, 1.5),
    lamp: makeHalo(28, '#ffc070', 0.24, 1.6), lampCore: makeHalo(8, '#fff0c8', 0.5, 1.3), side: makeHalo(3, '#ffd890', 0.35, 1),
    bulb: makeHalo(3, '#ffc870', 0.42, 1.1), bridge: makeHalo(44, '#ffb860', 0.09, 1.5),
    head: makeHalo(5, '#fff4d0', 0.5, 1.2), tail: makeHalo(3, '#ff3a30', 0.5, 1),
    lantern: makeHalo(20, '#ffc070', 0.28, 1.5), lanternCore: makeHalo(5, '#fff0c8', 0.6, 1.2),
    port: makeHalo(10, '#ffb860', 0.32, 1.4), hbwin: makeHalo(13, '#ffb860', 0.16, 1.5),
    bbGlow: makeHalo(24, '#ffb060', 0.06, 1.6), bbLamp: makeHalo(7, '#ffc070', 0.22, 1.3),   // the board's lamps: a window's warmth, well under the tower's glow
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  WISPS = [
    { c: makeWisps(47, 30, 9, { top: '#3a4478', body: '#232b56', bot: '#1c2349' }), y: 2, k: 1 },      // a high veil over the vane
    { c: makeWisps(21, 20, 9, { top: '#4a5690', body: '#2c3666', bot: '#232b56' }), y: 40, k: 1 },
    { c: makeWisps(33, 18, 7, { top: '#3e4a82', body: '#262f5c', bot: '#1f2750' }), y: 62, k: 2 },
    { c: makeWisps(59, 22, 9, { top: '#4c4c7e', body: '#343762', bot: '#2a2c54' }), y: 76, k: 3 },     // low scud behind the gables
  ];
  TOWER = renderTower();
  ROW = renderRow();
  for (const w of WINS)                                                                   // the rooms behind the ad board stay dark
    w.behindBoard = w.x + w.w > BB.x - 3 && w.x < BB.x + BB.w + 3 && w.y + w.h > BB.y - 3 && w.y < BB.y + BB.h + 3;
  // the windows people come to (LOOK_T): for each spot the nearest upper window that stays lit, clear of the elms and the board
  const free = WINS.filter(w => w.mode === 'on' && w.bare && !w.behindBoard
    && !TREES.some(T => Math.abs(w.cx - T.x) < T.rx + 6 && w.y + w.h > T.cy - T.ry - 5));
  LOOKERS = LOOK_T.map(([a, b, f]) => ({ a, b, w: free.sort((p, q) => Math.abs(p.cx - f * W) - Math.abs(q.cx - f * W)).shift() })).filter(l => l.w);
  BOARD_C = renderBoard(); ADS = [renderAd(false), renderAd(true)]; BIKE_SPR = sprite(BIKE_TOP, BIKE_KEY);
  PITCH = makePitch(BB.w, BB.h, { accent: '#b8e05a' });
  QUAY = renderQuay(); TRUNKS_C = renderTrees(); LAMPS_C = renderLamps(); BRIDGE = renderBridge();
  WATER_C = renderWater(); HBOAT = renderHouseboat(); PLANTS = renderRoofPlants();
  HERON_SPR = {};
  for (const k in HERON_POSES) HERON_SPR[k] = sprite(HERON_POSES[k], HERON_KEY);
  BOAT_C = renderBoat();
  RIDER_SPR = RIDERS.map(rd => [0, 1].map(fr => { const c = makeRider(rd, fr); return [c, flipC(c)]; }));
  DUCK_SPR = {};
  for (const k in DUCKS) DUCK_SPR[k] = [sprite(DUCKS[k], DUCK_KEY), sprite(DUCKS[k], DUCK_KEY, true)];
  SEED_SPR = [null, SEED_MID.map(s => sprite(s, SEED_KEY)), SEED_NEAR.map(s => sprite(s, NEAR_KEY))];
  MIST = [{ c: makeMist(3, 24, 0.9), y: 180, k: 1 }, { c: makeMist(7, 18, 0.7), y: 206, k: -2 }];
  BRANCH_C = renderBranch(); NEARQ_C = renderNearQuay(); VIG_C = renderVignette();
  RSCR = makeCanvas(W, NEARQ - WL);
  PUFF = [1, 2].map(rr => {
    const s = rr * 2 + 1, p = pix(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) { const d = Math.hypot(i - rr, y - rr) / (rr + 0.5); if (d < 1 && (1 - d) * 1.4 > bayer(i, y)) p.set(i, y, '#8a8e9e'); }
    return p.done();
  });
}

/* ================= Per-frame: sky, seeds ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e4e8f8';
  for (const s of STARS) {
    const a = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha = a * 0.3; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9);
  ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
  halo(HL.moonCore, MOON.x + 2, MOON.y, 0.7);
  for (const L of WISPS) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); }
}
const FAR_SEED = ['#6e7058', '#8a8c68', '#a0a27a'];
function drawSeeds(layer, t) {
  for (const s of SEEDS[layer]) {
    const { cyc, u } = cycle(t, s.n, s.ph);
    const land = layer === 1 ? 0.93 : 1, fu = Math.min(1, u / land);
    const calm = layer === 1 ? 1 - smooth(0.84, land, u) : 1, sw = wave(t, s.ks, s.p2) * calm;
    const bx = -30 + hash(s.id, cyc, 5) * (W + 40) + Math.min(u, land) * s.drift;
    const yEnd = layer === 0 ? 196 : layer === 1 ? WL + 6 + s.land * (NEARQ - WL - 14) : H + 10;
    if (layer === 1 && u >= land) {                                                      // lands on the water: a tiny ring
      const g = (u - land) / (1 - land), rr = 1 + g * 3, n = 8;
      ctx.globalAlpha = (1 - g) * 0.45; ctx.fillStyle = '#8a94b8';
      for (let j = 0; j < n; j++) { const a = j / n * TAU; ctx.fillRect(Math.round(bx + Math.cos(a) * rr), Math.round(yEnd + Math.sin(a) * rr * 0.4), 1, 1); }
      continue;
    }
    const x = bx + sw * s.amp, y = lerp(layer === 0 ? 92 : layer === 1 ? 40 : -10, yEnd, fu) - Math.abs(sw) * (layer + 1) * 1.3;
    if (x < -6 || x > W + 6) continue;
    const fl = Math.abs(Math.cos(TAU * (s.kf * t / LOOP + s.p4)));
    if (layer === 0) { ctx.globalAlpha = 0.7 * Math.min(1, fu * 8); dot(x, y, FAR_SEED[Math.floor(s.tone * 3)]); continue; }
    const spr = layer === 1 ? SEED_SPR[1][fl > 0.5 ? 0 : 1] : SEED_SPR[2][fl > 0.7 ? 0 : fl > 0.3 ? 1 : 2];
    ctx.globalAlpha = layer === 1 ? 0.85 * Math.min(1, fu * 6) : 0.92;
    ctx.drawImage(spr, Math.round(x) - (spr.width >> 1), Math.round(y) - (spr.height >> 1));
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: the tower and its carillon ================= */
function carillon(t) {
  let c = 0, b = 0;
  for (const q of QUARTERS) for (let i = 0; i < 4; i++) { const dt = t - (q + i * NOTE_DT); if (dt >= 0 && dt < 2.4) c = Math.max(c, Math.exp(-dt * 2.4)); }
  for (const tb of HOUR) { const dt = t - tb; if (dt >= 0 && dt < 3.2) b = Math.max(b, Math.exp(-dt * 1.4) * (1 - dt / 3.2)); }
  return { c, b };
}
function soundRing(cx, cy, rr, a) {
  const n = Math.floor(rr * 2.4);
  ctx.globalAlpha = a;
  for (let j = 0; j < n; j++) { const an = j / n * TAU; ctx.fillRect(Math.round(cx + Math.cos(an) * rr), Math.round(cy + Math.sin(an) * rr * 0.8), 1, 1); }
}
function drawTower(t, clock) {
  const { c, b } = carillon(t), cx = TWR.cx, bell = TWY + 63;                 // bell = the belfry's centre on screen
  halo(HL.tower, cx, TWY + 80, 0.95); halo(HL.towerCore, cx, TWY + 86, 0.75); halo(HL.flood, cx, 146, 0.9);
  ctx.drawImage(TOWER.c, TX0, TWY);
  ctx.globalAlpha = clamp(0.3 + 0.7 * Math.max(c, b), 0, 1); ctx.drawImage(TOWER.lit, TX0, TWY); ctx.globalAlpha = 1;
  hands(ctx, cx, TWY + 85, clock, 610, 2, 3, GOLD[5]);
  halo(HL.crown, cx, TWY + 31, 0.8 + 0.2 * wave(t, 8));
  halo(HL.belfry, cx, bell, 0.2 + 0.8 * Math.max(c, b));
  ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#ffd890';
  for (const q of QUARTERS) for (let i = 0; i < 4; i++) {
    const dt = t - (q + i * NOTE_DT);
    if (dt >= 0 && dt < 1.6) soundRing(cx, bell, 8 + dt * 16, (1 - dt / 1.6) * 0.34);
  }
  for (const tb of HOUR) { const dt = t - tb; if (dt >= 0 && dt < 2.8) { soundRing(cx, bell, 9 + dt * 40, (1 - dt / 2.8) * 0.3); soundRing(cx, bell, 7 + dt * 40, (1 - dt / 2.8) * 0.12); } }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}

/* ================= Per-frame: the row of houses ================= */
const LIT_NOW = new Uint8Array(512);
function winLit(w, t, board) {
  if (w === READER_WIN) return !(t >= READER_OFF[0] && t < READER_OFF[1]);
  if (board && w.behindBoard) return false;
  if (w.mode === 'on') return true;
  if (w.mode === 'off') return false;
  return hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 7) < w.p;
}
function clipBox(w, x, y, ww, hh, col) {
  const x0 = Math.max(w.x, x), y0 = Math.max(w.y, y), x1 = Math.min(w.x + w.w, x + ww), y1 = Math.min(w.y + w.h, y + hh);
  if (x1 > x0 && y1 > y0) { ctx.fillStyle = col; ctx.fillRect(x0, y0, x1 - x0, y1 - y0); }
}
function drawRow(t, board) {
  const D = ROW.dark, L = ROW.lit;
  ctx.drawImage(D.c, D.x, D.y);
  for (const w of WINS) {
    const on = winLit(w, t, board);
    LIT_NOW[w.id] = on ? 1 : 0;
    if (on) ctx.drawImage(L.c, w.x - L.x, w.y - L.y, w.w, w.h, w.x, w.y, w.w, w.h);
  }
  for (let i = SIDE_ELMS.length - 1; i >= 0; i--) {                                       // the side canal's elms, far to near
    const E = SIDE_ELMS[i], dx = E.o ? Math.round(E.amp * (1.5 * breeze(t, E.cx) + 0.45 * wave(t, E.k, 0.2))) : 0;
    if (dx) ctx.drawImage(E.o.c, E.o.x + dx, E.o.y);
  }
  // someone walks past the curtains of the second floor, window after window
  CURTAIN_PASS.forEach(([a, b], i) => {
    if (t < a || t >= b) return;
    const first = WALK_WINS[0], last = WALK_WINS[WALK_WINS.length - 1], u = (t - a) / (b - a);
    const x = Math.round(i & 1 ? lerp(last.x + last.w + 2, first.x - 4, u) : lerp(first.x - 4, last.x + last.w + 2, u));
    const bob = (Math.floor(t * 4) & 1);
    ctx.globalAlpha = 0.68;
    for (const w of WALK_WINS) { clipBox(w, x, w.y + 1 + bob, 2, 2, '#5a2e16'); clipBox(w, x - 1, w.y + 3 + bob, 4, 6, '#5a2e16'); }
    ctx.globalAlpha = 1;
  });
  ctx.globalAlpha = 0.8;
  for (const { a, b, w } of LOOKERS) {                                                    // someone comes to a window, looks out, goes
    if (t < a || t >= b) continue;
    const x = w.x + Math.round(t < a + 1.5 ? lerp(-3, 0, (t - a) / 1.5) : t > b - 1.5 ? lerp(0, w.w + 1, (t - b + 1.5) / 1.5) : 0);
    clipBox(w, x + 1, w.y + w.h - 6, 1, 2, '#3a1e10'); clipBox(w, x, w.y + w.h - 4, 3, 4, '#3a1e10');   // head and shoulders
  }
  ctx.globalAlpha = 1;
  if (t >= TV_ON[0] && t < TV_ON[1]) {
    const hv = hash(step(t, 1920), 11);
    ctx.fillStyle = ['#3a5aa8', '#4a6ac0', '#2a4a90', '#6a8ad8', '#20386e'][Math.floor(hv * 5)];
    ctx.fillRect(TV_WIN.x, TV_WIN.y, TV_WIN.w, TV_WIN.h);
    halo(HL.tv, TV_WIN.cx, TV_WIN.cy, 0.5 + 0.5 * hv);
  }
  for (const w of WINS) if (LIT_NOW[w.id]) halo(HL.win, w.cx, w.cy, 0.75);
  for (const s of SIDE_LAMPS) halo(HL.side, s.x, s.y, s.a);
  halo(HL.cafe, CAFE.x0 + 9, 176, 0.9 + 0.08 * wave(t, 30)); halo(HL.cafe, CAFE.x0 + 25, 176, 0.85);
  ctx.globalAlpha = 0.6 + 0.4 * hash(step(t, 1200), 21);
  dot(CAFE.x0 + 8, 177, '#fff4d0');
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: the ad board (warm wash, structure, the face, the lamps) ================= */
const BB_FLICK = [[88.2, 90.1], [201.6, 202.5]];          // the right-hand lamp sputters now and then
const bbLevel = t => { for (const [a, b] of BB_FLICK) if (t >= a && t < b) return hash(step(t, 4800), 13) < 0.45 ? 0.12 : 0.8; return 1; };
function drawAd(t, lv) {
  const X = BB.x + BIKE_AT.x, Y = BB.y + BIKE_AT.y;
  ctx.drawImage(ADS[0], BB.x, BB.y);
  if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(ADS[1], BB.x, BB.y); ctx.globalAlpha = 1; }
  const th = TAU * 48 * t / LOOP;                            // the wheels turn (48 turns per loop), the pedals at half speed
  ctx.fillStyle = DELFT.s;
  for (const [cx, cy] of WHEELS) for (let q = 0; q < 4; q++) {
    const a = th + q * Math.PI / 2;
    for (const k of [1, 2]) ctx.fillRect(X + cx + Math.round(Math.cos(a) * k), Y + cy + Math.round(Math.sin(a) * k), 1, 1);
  }
  const s = step(t, 960);                                    // the road runs back under the wheels
  ctx.fillStyle = DELFT.l;
  for (let i = 0; i < 19; i++) if (((i + s) & 3) < 2) ctx.fillRect(X - 1 + i, Y + 13, 1, 1);
  ctx.drawImage(BIKE_SPR, X, Y);
  const ph = th / 2, [bx, by] = CRANK;
  for (const [sg, col] of [[-1, DELFT.l], [1, DELFT.d]]) {
    ctx.fillStyle = col;
    ctx.fillRect(X + bx + Math.round(sg * Math.cos(ph) * 2), Y + by + Math.round(sg * Math.sin(ph) * 2), 1, 1);
  }
}
function drawBoard(t, pitch) {
  const lv = bbLevel(t);
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + 3, (0.85 + 0.12 * wave(t, 6, 0.3)) * (2 + lv) / 3);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) drawAd(t, lv);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => {
    const a = i === 2 ? lv : 1;
    if (a < 1) { ctx.globalAlpha = 0.85 * (1 - a); box(lx - 2, BB_LAMP_Y + 2, 5, 1, '#2a1c14'); ctx.globalAlpha = 1; }
    halo(HL.bbLamp, lx, BB_LAMP_Y + 3, a * (0.9 + 0.08 * wave(t, 500 + i * 41)));
  });
}

/* ================= Per-frame: quay life (cyclists, bulbs, lamps, a bat, leaves) ================= */
function drawRiders(t) {
  RIDERS.forEach((rd, i) => {
    if (t < rd.a || t >= rd.b) return;
    const u = (t - rd.a) / (rd.b - rd.a), spr = RIDER_SPR[i][Math.floor(t * 3) & 1][rd.dir > 0 ? 0 : 1], w = spr.width;
    const X = Math.round(rd.dir > 0 ? lerp(-w - 6, W + 6, u) : lerp(W + 6, -w - 6, u));
    const y = Math.round(deckY(X + (w >> 1))) - 12, lo = rd.kind === 2 ? 16 : 10;
    ctx.drawImage(spr, X, y);
    halo(HL.head, rd.dir > 0 ? X + lo + 1 : X + w - 2 - lo, y + (rd.kind === 2 ? 8 : 7), 0.9);
    halo(HL.tail, rd.dir > 0 ? X : X + w - 1, y + 8, 0.7);
  });
}
const BULB_COL = ['#ffd27a', '#ffc060', '#ffe8b0'];
function drawLights(t) {
  halo(HL.bridge, BR.cx, 182, 0.9);
  BULBS.forEach(b => {
    const I = 0.75 + 0.25 * wave(t, b.k, b.ph);
    ctx.globalAlpha = 0.7 + 0.3 * I; dot(b.x, b.y, BULB_COL[b.c]);
    ctx.globalAlpha = 1; halo(HL.bulb, b.x, b.y, I * 0.9);
  });
  LAMPS.forEach((lx, i) => { const I = 0.92 + 0.06 * wave(t, 300 + i * 7, i * 0.3); halo(HL.lamp, lx, 161, I); halo(HL.lampCore, lx, 161, I * 0.85); });
  ctx.fillStyle = LEAFLIT[3];
  for (const s of SHIMMER) { const v = wave(t, s.k, s.ph); if (v > 0.6) { ctx.globalAlpha = (v - 0.6) * 1.6; ctx.fillRect(s.x, s.y, 1, 1); } }
  for (const m of MOTHS) {                                                               // moths looping round the lanterns
    const ax = TAU * (m.kx * t / LOOP + m.px), dx = m.rx * Math.sin(ax) + Math.sin(TAU * m.kw * t / LOOP + m.px * 9);
    const y = m.y + m.ry * Math.sin(TAU * (m.ky * t / LOOP + m.py)) + 0.8 * Math.cos(TAU * (m.kw + 17) * t / LOOP + m.py * 9);
    const glass = Math.abs(dx) < 2 && Math.abs(y - 161) < 2.5;                            // dark against the glass, pale in its glow
    ctx.globalAlpha = glass ? 0.75 : 0.5 + 0.25 * Math.cos(ax);
    dot(m.x + dx, y, glass ? '#5a4226' : '#f4e6c0');
  }
  ctx.globalAlpha = 1;
  // a bat hunting moths around the lamp at the end of the bridge
  const bx = 362 + 12 * Math.sin(TAU * 5 * t / LOOP) + 5 * Math.sin(TAU * (13 * t / LOOP + 0.3));
  const by = 152 + 6 * Math.sin(TAU * (7 * t / LOOP + 0.1)) + 3 * Math.sin(TAU * 17 * t / LOOP);
  const X = Math.round(bx), Y = Math.round(by), up = step(t, 2400) & 1;
  ctx.fillStyle = '#07070c';
  ctx.fillRect(X, Y, 1, 1); ctx.fillRect(X - 1, Y + (up ? -1 : 1), 1, 1); ctx.fillRect(X + 1, Y + (up ? -1 : 1), 1, 1);
  ctx.fillRect(X - 2, Y + (up ? -1 : 0), 1, 1); ctx.fillRect(X + 2, Y + (up ? -1 : 0), 1, 1);
}

/* ================= Per-frame: the canal, ducks, the boat, mist ================= */
function drawCanal(t, seeds) {
  ctx.drawImage(WATER_C, 0, WL);
  reflectCanal(t, WL, NEARQ - WL, 0.52);
  ctx.globalAlpha = 0.16; box(0, WL, W, NEARQ - WL, '#050814'); ctx.globalAlpha = 1;
  ctx.fillStyle = '#ffe2a4';                                                            // the bulbs sparkle in the ripples
  for (const b of BULBS) {
    const ry = 2 * WL - 1 - b.y, k = ry - WL;
    const sh = Math.sin(TAU * 150 * t / LOOP + b.x * 0.9 + k * 0.7) + 0.6 * Math.sin(TAU * 97 * t / LOOP - k * 1.3 + b.x);
    if (sh < 0.25) continue;
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.7 + k * 0.045) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.7);
    ctx.globalAlpha = 0.55 * Math.min(1, sh) * (1 - k / 70);
    ctx.fillRect(b.x + off - (sh > 1.1 ? 1 : 0), ry, sh > 1.1 ? 3 : 1, 1);
  }
  ctx.globalAlpha = 1;
  for (let y = WL + 2, n = 0; y < NEARQ; y += 2, n++) for (let j = 0; j < 3; j++) {      // drifting troughs and glints
    const k = 2 + ((n * 7 + j * 5) % 7), len = 4 + Math.floor(hash(n, j, 41) * 14);
    const x = Math.round(frac(hash(n, j, 40) + k * t / LOOP) * (W + 40)) - 20;
    ctx.globalAlpha = j === 2 ? 0.16 : 0.32;
    box(x, y + (j & 1), len, 1, j === 2 ? '#7a84a8' : '#050914');
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, NEARQ);
  if (seeds) {
    ctx.globalAlpha = 0.55;
    for (const f of FLOATERS) dot(frac(f.x + f.k * t / LOOP) * (W + 20) - 10, f.y + Math.round(wave(t, 20, f.ph) * 0.6), '#9a9c74');
    ctx.globalAlpha = 1;
  }
}
/** mirror of the rows above the waterline, broken into shimmering horizontal slivers */
let RSCR = null;                                    // scratch band for the canal reflection (made in prerender)
function reflectCanal(t, top, rows, alpha) {
  const [sc, sx] = RSCR;
  sx.globalCompositeOperation = 'copy';
  sx.drawImage(ctx.canvas, 0, top - rows, W, rows, 0, 0, W, rows);
  for (let k = 0; k < rows; k++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 2.3) * (0.35 + k * 0.03) + Math.sin(TAU * 110 * t / LOOP - k * 3.1) * (0.45 + k * 0.02));
    ctx.globalAlpha = alpha * (1 - k / (rows + 24)) * (0.72 + 0.28 * Math.sin(TAU * 60 * t / LOOP + k * 1.7));
    ctx.drawImage(sc, 0, rows - 1 - k, W, 1, off, top + k, W, 1);
  }
  ctx.globalAlpha = 1;
}
function birdPos(b, t) {
  const ax = TAU * (b.kx * t / LOOP + b.px);
  return { x: b.cx + b.ax * Math.sin(ax), y: b.cy + b.ay * Math.sin(TAU * (b.ky * t / LOOP + b.py)), vx: Math.cos(ax) };
}
function waterRing(x, y, rr, a) {
  const n = 6 + Math.floor(rr * 2.5);
  ctx.globalAlpha = a;
  for (let j = 0; j < n; j++) { const an = j / n * TAU; ctx.fillRect(Math.round(x + Math.cos(an) * rr), Math.round(y + Math.sin(an) * rr * 0.36), 1, 1); }
  ctx.globalAlpha = 1;
}
function drawDucks(t, near) {
  ctx.fillStyle = '#8a94c0';
  const d0 = birdPos(BIRDS[0], DIVE.t0), d1 = birdPos(BIRDS[0], DIVE.t1 - 0.3);
  if (!near) for (const [px0, py0, t0] of [[d0.x, d0.y, DIVE.t0 + 0.25], [d1.x, d1.y, DIVE.t1 - 0.3]]) {
    const dt = t - t0;
    if (dt >= 0 && dt < 2.4) { waterRing(px0, py0 + 1, 1 + dt * 3.2, (1 - dt / 2.4) * 0.55); if (dt > 0.5) waterRing(px0, py0 + 1, dt * 3.2 - 1, (1 - dt / 2.4) * 0.3); }
  }
  for (const b of BIRDS) {
    if ((b.cy > BOAT_Y) !== near) continue;
    const { x, y, vx } = birdPos(b, t), X = Math.round(x), Y = Math.round(y), dir = vx >= 0 ? 1 : -1;
    if (b.kind === 'drake' && t >= DIVE.t0 + 0.3 && t < DIVE.t1 - 0.3) continue;       // under water
    ctx.globalAlpha = 0.3; ctx.fillStyle = '#6e78a4';                                    // V-shaped wake
    for (let k = 1; k < 7; k++) { const wx = X - dir * (2 + k * 2); ctx.fillRect(wx, Y + 1 + (k >> 1), 1, 1); ctx.fillRect(wx, Y + 1 - (k >> 2), 1, 1); }
    ctx.globalAlpha = 0.45; box(X - 3, Y + 1, 7, 1, '#2a3258');
    ctx.globalAlpha = 1;
    const spr = DUCK_SPR[b.kind][dir > 0 ? 0 : 1];
    const tipped = b.kind === 'drake' && t >= DIVE.t0 && t < DIVE.t0 + 0.3;
    ctx.drawImage(spr, 0, 0, 7, tipped ? 2 : 4, X - 3, Y - 3 + (tipped ? 2 : 0), 7, tipped ? 2 : 4);
  }
}
function drawBoat(t) {
  if (t < BOAT.t0 || t >= BOAT.t1) return;
  const u = (t - BOAT.t0) / (BOAT.t1 - BOAT.t0), bx = Math.round(lerp(W + 8, -BOAT_W - 8, u)), by = BOAT_Y - BOAT_H + 1;
  for (let j = 0; j < 14; j++) {                                                         // reflection
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 2.3) * (0.4 + j * 0.06) + Math.sin(TAU * 110 * t / LOOP - j * 3.1) * 0.5);
    ctx.globalAlpha = 0.32 * (1 - j / 14);
    ctx.drawImage(BOAT_C, 0, BOAT_H - 1 - j, BOAT_W, 1, bx + off, BOAT_Y + 1 + j, BOAT_W, 1);
  }
  const st = step(t, 1440);
  for (let k = 0; k < 26; k++) {                                                         // wake behind the stern, bow wave
    if (hash(k, st, 17) > 0.8 - k * 0.016) continue;
    ctx.globalAlpha = 0.5 * (1 - k / 26);
    dot(bx + 60 + k * 3, BOAT_Y + 1 + k * 0.34, k < 4 ? '#c8ccdc' : '#6e78a0');
    if (k > 3) dot(bx + 59 + k * 3, BOAT_Y - k * 0.1, '#5a6490');
  }
  ctx.globalAlpha = 0.6; dot(bx, BOAT_Y - 1, '#c8ccdc'); dot(bx + 1, BOAT_Y + 1, '#9aa0c0'); dot(bx + 3, BOAT_Y + 1, '#7a84a8');
  ctx.globalAlpha = 1;
  ctx.drawImage(BOAT_C, bx, by);
  if (t >= 128 && t < 131.5) { const lift = t < 129 || t > 130.6 ? 0 : 1; dot(bx + 33, by + 5 - lift, '#e0a878'); dot(bx + 34, by + 4 - lift, '#f0e8c8'); }   // a toast
  let I = 0.9 + 0.1 * wave(t, 400);
  for (const [a, b] of BOATLAMP_FLICK) if (t >= a && t < b) I = hash(Math.floor(t * 20), 5) > 0.4 ? 0.95 : 0.35;
  halo(HL.lantern, bx + LANT.x, by + LANT.y, I); halo(HL.lanternCore, bx + LANT.x, by + LANT.y, I);
  streak(ctx, t, bx + LANT.x, 3, '#ffd890', 0.42 * I, 9, BOAT_Y + 1, NEARQ);
}
function drawMist(t) {
  for (const m of MIST) {
    const off = scroll(t, m.k, W);
    ctx.globalAlpha = (m.k > 0 ? 0.4 : 0.26) * (0.75 + 0.25 * wave(t, 3, m.k * 0.1));
    ctx.drawImage(m.c, off - W, m.y); ctx.drawImage(m.c, off, m.y);
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: the houseboat, its garden and the heron ================= */
const HB_PASS = [[58, 61.5], [182, 186]];
function drawSway(o, t) {
  const h = o.c.height;
  let j = 0;
  const offAt = j2 => { const f = clamp((HB.roof - 7 - (o.y + j2)) / 16, 0, 1); return Math.round((1.2 * wave(t, 16, 0.3) + 0.5 * wave(t, 41, 0.6)) * f); };
  while (j < h) {
    const off = offAt(j);
    let j2 = j + 1;
    while (j2 < h && offAt(j2) === off) j2++;
    ctx.drawImage(o.c, 0, j, o.c.width, j2 - j, o.x + off, o.y + j, o.c.width, j2 - j);
    j = j2;
  }
}
/** the elm branch bobs in the breeze: its free tips (left) move more than the end held by the tree (right) */
function drawBranch(t) {
  const o = BRANCH_C, w = o.c.width, h = o.c.height, sw = 1.4 * wave(t, 12, 0.2) + 0.6 * wave(t, 29, 0.5);
  const offAt = i => Math.round(sw * clamp((W - (o.x + i)) / 150, 0, 1));
  let i = 0;
  while (i < w) {
    const off = offAt(i);
    let i2 = i + 1;
    while (i2 < w && offAt(i2) === off) i2++;
    ctx.drawImage(o.c, i, 0, i2 - i, h, o.x + i, o.y + off, i2 - i, h);
    i = i2;
  }
}
function heronPose(t) {
  for (const [a, b, p] of HERON_T) if (t >= a && t < b) return p === 'preen' ? ((step(t, 400) & 1) ? 'preen2' : 'preen') : p;
  return 'stand';
}
function drawHouseboat(t) {
  blit(HBOAT);
  reflect(ctx, t, { top: HB.wl, rows: NEARQ - HB.wl, squash: 1, alpha: 0.32, x: 0, w: HB.x1 + 2, k1: 40, k2: 90 });
  for (const [a, b] of HB_PASS) if (t >= a && t < b) {                                  // someone crosses the living room
    const x = Math.round(lerp(57, 71, (t - a) / (b - a))), g = { x: 60, y: 214, w: 10, h: 11 };
    clipBox(g, x, 215, 2, 2, '#3a2012'); clipBox(g, x - 1, 217, 4, 8, '#3a2012');
  }
  const cb = breeze(t, HB_CURT), hem = cb > 0.78 ? 2 : cb > 0.5 ? 1 : 0;                  // the open window's curtains swing downwind
  if (hem) {
    box(HB_CURT + 3, 224 - hem * 3, 1, hem * 3 + 1, '#d08848');                           // the left one's hem covers the room,
    box(HB_CURT + 7, 224 - hem * 3, 1, 4, '#f6bc6c'); box(HB_CURT + 7, 222, 1, 3, '#dc9448');   // the right one's lets it show
    if (hem > 1) { box(HB_CURT + 4, 222, 1, 3, '#d08848'); box(HB_CURT + 7, 219, 1, 1, '#e8c88c'); }
  }
  for (const w of HB_WINS) if (w.kind !== 'dark') halo(HL.hbwin, w.x + 5, 219, 0.8);
  const pl = 0.72 + 0.14 * wave(t, 190, 0.2) + 0.14 * (hash(step(t, 1920), 3) - 0.5);
  halo(HL.port, 120, 240, pl); ctx.globalAlpha = pl; dot(120, 239, '#fff2c8'); ctx.globalAlpha = 1;
  drawSway(PLANTS, t);
  const spr = HERON_SPR[heronPose(t)];
  ctx.drawImage(spr, HERON.x, HERON.y - spr.height + 1);
  for (let k = 0; k < 6; k++) {                                                           // a wisp from the stove
    const u = frac(t * 20 / LOOP + k / 6), rr = u < 0.4 ? 0 : 1;
    const x = 31 + u * 16 + wave(t, 13, k * 0.17) * 1.2 * u, y = 191 - u * 20;
    ctx.globalAlpha = 0.22 * Math.min(1, u * 6) * (1 - u);
    ctx.drawImage(PUFF[rr], Math.round(x) - rr - 1, Math.round(y) - rr - 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the board; billboard: false leaves the board out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, clock, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawTower(t, clock);
  drawRow(t, billboard);
  if (billboard) drawBoard(t, pitch);
  if (wx.seeds) drawSeeds(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc[0], reducedMotion);
  blit(QUAY.back);
  blit(BRIDGE.body);
  drawRiders(t);
  blit(BRIDGE.rail);
  blit(QUAY.front);
  drawElms(t);
  blit(LAMPS_C);
  drawLights(t);
  drawCanal(t, wx.seeds);
  drawWeather(ctx, wx.shared, 1, t, wxc[1], reducedMotion);        // behind the ducks and the boat, so nothing lands on the boat
  drawDucks(t, false);
  drawBoat(t);
  drawDucks(t, true);
  drawMist(t);
  if (wx.seeds) drawSeeds(1, t);
  drawHouseboat(t);
  blit(NEARQ_C);
  drawBranch(t);
  if (wx.seeds) drawSeeds(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc[2], reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'amsterdam',
  name: 'Amsterdam',
  tagline: 'Bridge lights on the canal',
  timeZone: 'Europe/Amsterdam',
  accent: '#b8e05a',
  focusX: 262,
  landmark: [TWR.cx - 11, TWR.cx + 12],                // the Westertoren
  landmarkTop: 3,                                      // the row of the vane's pole, which landscape crops keep
  billboard: BB,
  perch: { dx: 12, dy: -1 },                           // the What's new pigeon (site/js/ui/pigeon.js): between the lamps
  ambience: { water: 0.4, wind: 0.12, crowd: 0.12, birds: 0.05, crickets: 0.05 },
  events: [
    ...QUARTERS.map(t => ({ t, type: 'chime', x: TWR.cx, gain: 0.5 })),
    ...HOUR.map(t => ({ t, type: 'bell', x: TWR.cx, gain: 0.5 })),
    { t: BOAT.t0, type: 'boat', dur: BOAT.t1 - BOAT.t0, pan: [0.9, -0.9], gain: 0.45 },
    { t: DIVE.t0 + 0.3, type: 'splash', x: Math.round(birdPos(BIRDS[0], DIVE.t0).x), gain: 0.35 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
